import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
  createContext,
  useContext,
} from "react";
import { ref, update, remove, onValue } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { database, firestore, functions } from "../../firebaseConfig";
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from "../../utils/cloudFunctions";
import { useCopyClones } from "../CopyCloneAI/useCopyClones";
import { updateClone } from "../CopyCloneAI/cloneUtils";
import useCustomFields from "../../hooks/useCustomFields";
import useCreditBalance from "../../hooks/useCreditBalance";
// Reuse the same recipient helpers the live AI modes use, so the test
// preview maps a Firestore lead to the prompt context EXACTLY the way a
// real send would. Drift here = preview lies about real output.
import { getRecipientInfoForContact, getFirstDmName } from "../../shared/enums";
import { buildContactBlock, buildSenderBlock } from "./buildVoiceClonePayload";
import {
  STRUCTURE_LIBRARY,
  STRUCTURE_BY_ID,
  LENGTH_TWEAKS,
  LENGTH_TWEAK_BY_ID,
  resolveLengthTweakId,
  computeBeatBreakdown,
  computeTargetWords,
  sanitizeCustomWords,
  CUSTOM_WORDS_DEFAULT,
  LENGTH_HARD_CEILING,
  LENGTH_HARD_FLOOR,
  STANDARD_PERSONALIZATION_FIELDS,
  DEFAULT_ENABLED_STANDARD_KEYS,
  INFO_ENHANCEMENTS,
  QUALITY_TIERS,
  QUALITY_TIER_DEFAULT,
  resolveQualityTier,
  formatCreditCost,
  CREDIT_ACTION_PREVIEW,
  TEMPLATE_MODES,
  generateVariantId,
  createVariant,
  CAMPAIGN_LANGUAGES,
  CAMPAIGN_LANGUAGE_DEFAULT,
  resolveEditorUILanguage,
  INFO_ADDON_PRESETS,
  INFO_ADDON_PRESET_BY_ID,
  createInfoAddon,
  VOICE_ADHERENCE_LEVELS,
  BRIEF_ADHERENCE_LEVELS,
  VOICE_ADHERENCE_DEFAULT,
  BRIEF_ADHERENCE_DEFAULT,
  CREATIVITY_LEVELS,
  CREATIVITY_DEFAULT,
  SIGN_OFF_IDENTITY_MODES,
  resolveSignOffIdentity,
  GREETING_MODES,
  GREETING_DEFAULT_WITH_FIRST_NAME,
  GREETING_DEFAULT_WITHOUT_FIRST_NAME,
  GREETING_COMPANY_MENTION,
  GREETING_COMPANY_CONTEXT_LEVELS,
  resolveGreeting,
  applyGreetingPlaceholders,
  buildShortBusinessName,
  computeVariantConflicts,
  computeTemplateConflicts,
  STRUCTURE_SOURCES,
  resolveStructureSource,
} from "./cloneTemplateUtils";
import "./VoiceCloneTemplateEditor.css";
import CallContextSettings from "../CallContextSettings";
import { normalizeCallContext } from "../callContextConfig";

// =============================================================================
// EDITOR I18N — only English (default) + German. Any other campaign language
// falls back to English (handled by `resolveEditorUILanguage`).
//
// Strings are flat key/value pairs scoped to the editor only; deeper
// sub-components consume them via the `useT()` hook (see EditorLangContext).
// =============================================================================

const EDITOR_TRANSLATIONS = {
  en: {
    // Tabs — icon + single-word label only (lean chrome).
    tabEssentials: "Basics",
    tabPersonalization: "Personalization",
    tabDetails: "Details",

    // CopyTwin section (the AI copywriter twin that writes every email)
    voiceCloneTitle: "CopyTwin",
    voiceCloneHint: "The copywriter twin that writes every email in this campaign.",
    voiceCloneEmpty:
      "No twins yet. Create one in <strong>Email → CopyTwin AI</strong> first.",
    voiceCloneLoading: "Loading twins…",
    voiceCloneUntrained: "Untrained twins",
    voiceCloneUntrainedHint:
      "Trainable but won't write in an authentic voice yet.",
    voiceCloneSelectedSuffix: "will write every email in this campaign.",
    voiceDescTitle: "Voice description",
    voiceDescHint:
      "A short style brief in your own words: who you write to, what register, what angle. The AI uses this as its top-priority frame. The quote above is only what we auto-learned from your samples — this is where you steer it.",
    voiceDescPlaceholder:
      "e.g. I write to busy B2B founders. Direct and warm, no corporate fluff. I open with a specific observation and close with a low-pressure question.",
    voiceDescAutofill: "Use what we learned from your voice",
    voiceDescSavedToClone: "Saved to this CopyTwin (applies everywhere it's used)",

    // Language section
    languageTitle: "Campaign language",
    languageHint: "Language the emails are written in.",

    // Campaign brief (Special / Pain / Goal). References live in Details.
    briefTitle: "Campaign brief",
    briefHint: "Three answers that focus the campaign.",
    briefRefsLabel: "References to use",
    briefRefsMicro:
      "Proof points the AI may pull from — clients, results, social proof.",
    briefRefsPlaceholder:
      "e.g. 200+ B2B SaaS clients including Notion and Linear · case study with Acme: 3.2× reply rate in 60 days · TechCrunch feature 2024.",
    briefSpecialLabel: "What makes you special",
    briefSpecialMicro:
      "Your edge in one breath — the reason this lands instead of any other email.",
    briefSpecialPlaceholder:
      "e.g. We're the only outbound platform that learns each sender's voice from real samples — replies that actually sound like the founder, not a template.",
    briefPainLabel: "Pain points of your audience",
    briefPainMicro:
      "What hurts your recipients today — the pain you solve. Fuels the hook so the email lands where it matters.",
    briefPainPlaceholder:
      "e.g. Cold emails sound generic and get ignored · reps spend hours rewriting templates per lead · reply rates below 2% despite high volume · no idea which variant actually works.",
    briefGoalLabel: "Goal of these emails",
    briefGoalMicro:
      "What success looks like — drives the CTA tone.",
    goalPlaceholder:
      "e.g. Get a short reply. Curious tone tied to their portfolio. One yes/no question at the end.",
    goalQuickFill: "Quick-fill",

    // Info add-ons
    addonsTitle: "Info add-ons",
    addonsHint: "Optional info pools the AI can pull from.",
    addonsAddBtn: "Add info pool",
    addonsCustomLabel: "Custom info",
    addonsCustomPlaceholder: "Name this info pool…",
    addonsItemPlaceholder: "Add an item and press Enter",
    addonsRemoveTitle: "Remove this info pool",
    // Preset pill labels — keys mirror INFO_ADDON_PRESETS ids so the renderer
    // can resolve them with t(`addonPreset_${preset.id}`) without a manual map.
    addonPreset_case_studies:    "Case studies",
    addonPreset_testimonials:    "Testimonials",
    addonPreset_notable_clients: "Notable clients",
    addonPreset_stats:           "Stats & metrics",
    addonPreset_press:           "Press mentions",
    addonPreset_credentials:     "Credentials",
    addonPreset_background:      "Background",
    addonPreset_objections:      "Objections + answers",
    addonPreset_trust:           "Trust signals",
    addonPreset_recent_wins:     "Recent wins",
    addonPreset_custom:          "Custom info",
    goalSugReplyLabel: "Get a reply",
    goalSugReplyText:
      "Get a short reply from the recipient. Curiosity over pitch. The CTA should be answerable in one sentence and feel low-friction.",
    goalSugMeetingLabel: "Book a meeting",
    goalSugMeetingText:
      "Book a 15-minute intro call. Make the value concrete enough to justify the time, but keep the ask small. End with a specific time-based CTA.",
    goalSugClickLabel: "Drive a click",
    goalSugClickText:
      "Get the recipient to click a link to a demo / landing page / case study. The body should make the click feel like the path of least resistance.",
    goalSugWarmupLabel: "Warm-up touch",
    goalSugWarmupText:
      "Open a relationship without pitching. Add genuine value or insight. No hard CTA — just leave the door open.",

    // Sections — short inline hints. The deeper detail lives in the
    // sub-component UIs themselves (e.g. "Manage in Admin" inside FieldSelector).
    rhythmTitle: "Paragraph rhythm",
    rhythmHint:
      "Override the visual rhythm for this campaign. Auto follows the clone's natural style learned from samples.",
    rhythmAutoLabel: "Auto",
    rhythmAutoHint: "From clone samples",
    rhythmTightLabel: "Tight",
    rhythmTightHint: "Short one-liners",
    rhythmStandardLabel: "Standard",
    rhythmStandardHint: "Short paragraphs",
    rhythmAiryLabel: "Airy",
    rhythmAiryHint: "Spacious flowing prose",

    contactDataTitle: "Contact data",
    contactDataHint: "Fields the AI may use to personalize.",

    callContextTitle: "Call Context",
    callContextHint: "The email builds on what was said in the contact's logged calls and meetings (Notes).",

    // Sign-off identity + greeting rules (per-template addressing controls)
    signOffIdentityTitle:    "Sign-off identity",
    signOffIdentityHint:     "Who the email is signed as (overrides the workspace sender).",
    signOffIdentityAuto:     "Use the active sender",
    signOffIdentityAutoDesc: "Sign as whichever sender account the email is sent from.",
    signOffIdentityCustom:   "Custom",
    signOffIdentityCustomDesc:
      "Always sign as the person below, regardless of which sender account is used.",
    signOffIdentityNameLabel: "Signature name",
    signOffIdentityNameHint:
      "Used verbatim in the email signature. Type the first name only (e.g. \"Jan\") or include the last name (e.g. \"Jan Schubert\") — spaces are allowed.",
    signOffIdentityNamePlaceholder: "e.g. Jan Schubert",
    signOffIdentityRoleLabel: "Role (optional)",
    signOffIdentityRolePlaceholder: "e.g. Co-Founder",

    greetingTitle:    "Greeting rules",
    greetingHint:     "Lock the opening line so the AI can't drift on addressing.",
    greetingModeAuto: "Let voice DNA decide",
    greetingModeAutoDesc:
      "Open the email in whatever greeting style the clone naturally uses.",
    greetingModeConfigured: "Use these greetings",
    greetingModeConfiguredDesc:
      "Pin the exact opening line per case (decision maker known vs unknown).",
    greetingWithFirstNameLabel: "When we know the recipient first name",
    greetingWithFirstNameHint:
      "Used for personal addresses like dieter@acme.com. Placeholders: {{firstName}}, {{lastName}}, {{recipientName}}, {{businessName}}, {{businessNameShort}}.",
    greetingWithFirstNamePlaceholder: "e.g. Hi {{firstName}},",
    greetingWithoutFirstNameLabel: "When we only have a generic / info@ address",
    greetingWithoutFirstNameHint:
      "Used for info@, contact@ etc. Placeholders: {{businessName}}, {{businessNameShort}}. Short form drops legal suffixes (GmbH, AG, Inc., …) automatically.",
    greetingWithoutFirstNamePlaceholder: "e.g. Hallo {{businessNameShort}}-Team,",
    greetingPreviewHeader: "Live preview",
    greetingPreviewWithName: "With first name:",
    greetingPreviewWithoutName: "Generic / no DM:",

    // Greeting personalization controls — apply to BOTH greeting modes.
    greetingPersonalizationTitle: "Greeting personalization",
    greetingPersonalizationHint:
      "Steer how much the AI ties the opener to the recipient's company — works in both modes above.",
    greetingCompanyMentionLabel: "Mention the company in the greeting",
    greetingCompanyMentionAuto:    "Let the clone decide",
    greetingCompanyMentionInclude: "Always include",
    greetingCompanyMentionExclude: "Never include",
    greetingCompanyMentionHint:
      "Include uses the brandable short form (e.g. \"Müller Optik\" instead of \"Müller Optik GmbH\").",
    greetingContextLevelLabel: "Company context depth",
    greetingContextLevelHint:
      "How much the opener leans on industry / product / role signals.",
    greetingContext_minimal_label:  "Minimal",
    greetingContext_minimal_desc:   "Plain greeting, no company-specific framing.",
    greetingContext_light_label:    "Light",
    greetingContext_light_desc:     "Mention the company briefly if it fits naturally.",
    greetingContext_grounded_label: "Grounded",
    greetingContext_grounded_desc:  "Ground the opener in the recipient's company context.",
    greetingContext_deep_label:     "Deep",
    greetingContext_deep_desc:      "Lean into specific industry / product / role signals.",

    enhancementsTitle: "Optional enhancements",
    enhancementsHint: "Extras on top of contact data.",
    enhWebResearchLabel: "Live web research",
    enhWebResearchDesc:
      "Pull fresh context from the recipient's website at generation time (homepage signals, recent posts).",
    enhRecipientHistoryLabel: "Prior interaction history",
    enhRecipientHistoryDesc:
      "If we've contacted them before, reference past touchpoints respectfully.",
    enhSenderContextLabel: "Sender context",
    enhSenderContextDesc:
      "Inject the sender's role / business context for relevance framing.",
    factsTitle: "Campaign facts",
    factsHint: "Whitelist of what the AI may claim.",
    factsPlaceholder:
      "e.g. 10k+ users since 2019, featured in TechCrunch, free 14-day trial",
    anglesTitle: "Angles to lean into",
    anglesHint: "Themes the AI weaves across variants.",
    anglesPlaceholder:
      "e.g. ROI in 30 days, no integrations needed, peer endorsements",
    extraRulesTitle: "Extra rules",
    extraRulesHint:
      "Free space for additional must-follow rules or notes the AI should respect on top of the brief.",
    extraRulesPlaceholder:
      "e.g. Never mention pricing in the first email · Always offer a 15-min call · Keep tone casual, no buzzwords · Reference Q4 launch only if recipient is in retail.",
    subjectsTitle: "Subject lines",
    subjectsHint: "Manual subjects or auto-written per recipient.",
    structuresTitle: "Structure variants",
    structuresHint: "A/B test structures in parallel.",
    structureSourceTwin: "Copy from CopyTwin",
    structureSourceCustom: "Custom structures",
    structureSourceTwinDesc:
      "The email mirrors your CopyTwin's own structure and length, learned from its training emails. The structure variants below are optional.",
    structureSourceCustomDesc:
      "Use the structure variants below (curated frameworks you can A/B test).",
    structureSourceTwinUntrained:
      "Train this CopyTwin first (add sample emails) so it has a structure to copy.",
    structureVariantsOptional: "Optional in CopyTwin mode",
    taboosTitle: "Campaign taboos",
    taboosHint: "Phrases that must never appear.",
    taboosPlaceholder:
      'e.g. "discount", "urgency", competitor mentions',

    // Test recipient picker (preview panel)
    testRecipientLabel:        "Test recipient",
    testRecipientHint:         "Generate against a real lead so you can judge the output.",
    testRecipientLoading:      "Loading leads…",
    testRecipientNone:         "No leads available — using sample data",
    testRecipientRoll:         "Roll random",
    testRecipientChange:       "Change recipient",
    testRecipientFallback:     "Sample (Müller Optik)",
    testRecipientPickFromList: "Pick from list",
    testRecipientNotContactedOnly: "Only not-contacted leads",

    // Adherence dials (Voice + Brief)
    adherenceTitle:            "Adherence",
    adherenceHint:             "How strictly the model sticks to the voice and to the brief.",
    voiceAdherenceLabel:       "Voice adherence",
    voiceAdherenceHint:        "How tightly the email mirrors the writer's style patterns.",
    briefAdherenceLabel:       "Brief adherence",
    briefAdherenceHint:        "How thoroughly every brief item must show up in the email.",
    voice_free_label:          "Free",
    voice_free_desc:           "Use the voice as a soft inspiration. The model is freer to fit the recipient.",
    voice_balanced_label:      "Balanced",
    voice_balanced_desc:       "Voice patterns guide rhythm and word choice but don't constrain every sentence.",
    voice_strict_label:        "Strict",
    voice_strict_desc:         "Most sentences must reflect at least one DNA pattern.",
    voice_mirror_label:        "Mirror clone",
    voice_mirror_desc:         "Maximum voice fidelity — reads as if the clone wrote it. Generic phrasing must be rewritten.",
    brief_loose_label:         "Loose",
    brief_loose_desc:          "Pull from the brief only when it strengthens the email. May skip points.",
    brief_balanced_label:      "Balanced",
    brief_balanced_desc:       "Cover the most relevant brief points naturally. Not every item has to appear.",
    brief_strict_label:        "Strict",
    brief_strict_desc:         "Every brief item (references, special, pain, goal, angles) must be reflected.",
    creativityLabel:           "Creativity",
    creativityHint:            "How much wording, openings and angles vary from one email to the next.",
    creativity_precise_label:  "Precise",
    creativity_precise_desc:   "Very consistent. Sticks closely to the proven wording — least variety.",
    creativity_balanced_label: "Balanced",
    creativity_balanced_desc:  "A healthy mix of consistency and fresh wording. Good default.",
    creativity_high_label:     "Creative",
    creativity_high_desc:      "More variety in wording, openings and angles between emails.",
    creativity_max_label:      "Max",
    creativity_max_desc:       "Maximum variety. Each email takes a noticeably different take.",

    // Header — back / type pill / autosave
    headerBack:                "Back",
    headerNamePlaceholder:     "Untitled campaign",
    headerType_intro:          "Introduction",
    headerType_follow_up_1:    "Follow-up 1",
    headerType_follow_up_2:    "Follow-up 2",
    headerType_follow_up_3:    "Follow-up 3",
    headerType_custom_reply:   "Custom Reply",
    headerType_default:        "Template",
    headerStatSent:            "Sent",
    headerStatReplies:         "Replies",
    headerStatRate:            "Reply rate",
    headerToggleActive:        "Active in outreach",
    headerToggleInactive:      "Inactive",
    autoSaveSaving:            "Saving…",
    autoSaveSaved:             "Saved",
    autoSaveDirty:             "Saved",
    autoSaveError:             "Save failed",
    autoSaveDisabled:          "Paused",

    // Danger zone
    dangerTitle: "Delete this template",
    dangerHint:
      "Removes the campaign config and stats. The CopyTwin itself is not affected.",
    dangerBtn: "Delete template",
    confirmTitle: "Delete this template?",
    confirmText:
      "This will remove the campaign config and all per-variant stats. The CopyTwin itself stays intact.",
    confirmCancel: "Cancel",
    confirmDelete: "Delete",
  },
  de: {
    tabEssentials: "Basics",
    tabPersonalization: "Personalisierung",
    tabDetails: "Details",

    voiceCloneTitle: "CopyTwin",
    voiceCloneHint: "Der Copywriter-Zwilling, der jede Mail dieser Kampagne schreibt.",
    voiceCloneEmpty:
      "Noch keine Zwillinge. Lege zuerst einen unter <strong>Email → CopyTwin AI</strong> an.",
    voiceCloneLoading: "Zwillinge werden geladen…",
    voiceCloneUntrained: "Untrainierte Zwillinge",
    voiceCloneUntrainedHint:
      "Können trainiert werden — schreiben aber noch nicht in deiner echten Stimme.",
    voiceCloneSelectedSuffix:
      "schreibt jede Mail in dieser Kampagne.",
    voiceDescTitle: "Voice description",
    voiceDescHint:
      "Ein kurzer Stil-Brief in deinen eigenen Worten: an wen du schreibst, welches Register, welcher Blickwinkel. Die KI nutzt das als wichtigsten Rahmen. Das Zitat oben ist nur das, was wir automatisch aus deinen Beispielen gelernt haben — hier steuerst du selbst.",
    voiceDescPlaceholder:
      "z. B. Ich schreibe an vielbeschäftigte B2B-Gründer. Direkt und warm, ohne Corporate-Floskeln. Ich starte mit einer konkreten Beobachtung und schließe mit einer lockeren Frage.",
    voiceDescAutofill: "Übernimm, was wir aus deiner Stimme gelernt haben",
    voiceDescSavedToClone:
      "Beim CopyTwin gespeichert (gilt überall, wo er genutzt wird)",

    languageTitle: "Kampagnen­sprache",
    languageHint: "Sprache, in der die Mails geschrieben werden.",

    briefTitle: "Kampagnen-Brief",
    briefHint: "Drei Antworten, die die Kampagne fokussieren.",
    briefRefsLabel: "Referenzen, die du nutzen willst",
    briefRefsMicro:
      "Proof Points, aus denen die KI ziehen darf — Kunden, Ergebnisse, Social Proof.",
    briefRefsPlaceholder:
      "z.B. 200+ B2B-SaaS-Kunden u.a. Notion und Linear · Case Study mit Acme: 3,2× höhere Antwortrate in 60 Tagen · TechCrunch-Feature 2024.",
    briefSpecialLabel: "Was uns besonders macht",
    briefSpecialMicro:
      "Dein Edge in einem Atemzug — warum das hier ankommt und keine andere Mail.",
    briefSpecialPlaceholder:
      "z.B. Wir sind die einzige Outbound-Plattform, die die Stimme jedes Senders aus echten Beispielen lernt — Antworten, die wirklich nach dem Gründer klingen, nicht nach Template.",
    briefPainLabel: "Pain Points deiner Zielgruppe",
    briefPainMicro:
      "Was deinem Gegenüber heute weh tut — die Probleme, die du löst. Liefert der KI den Hook, damit die Mail dort landet, wo es wirklich brennt.",
    briefPainPlaceholder:
      "z.B. Kalte Mails klingen generisch und werden ignoriert · Vertrieb verbringt Stunden mit Template-Anpassungen pro Lead · Antwortraten unter 2% trotz hohem Volumen · keine Übersicht welche Variante wirklich performt.",
    briefGoalLabel: "Ziel dieser Mails",
    briefGoalMicro:
      "Wie Erfolg aussieht — bestimmt den CTA-Ton.",
    goalPlaceholder:
      "z.B. Kurze Antwort holen. Neugier-Ton auf ihr Portfolio. Eine Ja/Nein-Frage am Ende.",
    goalQuickFill: "Schnellauswahl",

    addonsTitle: "Info-Add-ons",
    addonsHint: "Optionale Info-Pools, aus denen die KI ziehen kann.",
    addonsAddBtn: "Info-Pool hinzufügen",
    addonsCustomLabel: "Custom Info",
    addonsCustomPlaceholder: "Name für diesen Info-Pool…",
    addonsItemPlaceholder: "Eintrag hinzufügen, Enter drücken",
    addonsRemoveTitle: "Info-Pool entfernen",
    addonPreset_case_studies:    "Case Studies",
    addonPreset_testimonials:    "Testimonials",
    addonPreset_notable_clients: "Bekannte Kunden",
    addonPreset_stats:           "Zahlen & Metriken",
    addonPreset_press:           "Presse-Erwähnungen",
    addonPreset_credentials:     "Qualifikationen",
    addonPreset_background:      "Hintergrund",
    addonPreset_objections:      "Einwände + Antworten",
    addonPreset_trust:           "Trust Signale",
    addonPreset_recent_wins:     "Aktuelle Erfolge",
    addonPreset_custom:          "Custom Info",
    goalSugReplyLabel: "Antwort bekommen",
    goalSugReplyText:
      "Eine kurze Antwort des Empfängers bekommen. Neugier statt Pitch. Der CTA sollte in einem Satz beantwortbar sein und niedrigschwellig wirken.",
    goalSugMeetingLabel: "Meeting buchen",
    goalSugMeetingText:
      "Ein 15-Minuten-Intro-Call buchen. Den Mehrwert konkret machen, damit die Zeit gerechtfertigt wirkt — aber der Ask bleibt klein. Am Ende ein konkreter, zeitbasierter CTA.",
    goalSugClickLabel: "Klick auslösen",
    goalSugClickText:
      "Den Empfänger dazu bringen, auf einen Link zu klicken (Demo / Landingpage / Case Study). Der Body soll den Klick als den einfachsten Weg darstellen.",
    goalSugWarmupLabel: "Warmup-Touch",
    goalSugWarmupText:
      "Eine Beziehung öffnen, ohne zu pitchen. Echten Mehrwert oder Insight liefern. Kein harter CTA — die Tür offen lassen.",

    rhythmTitle: "Absatz-Rhythmus",
    rhythmHint:
      "Überschreibt den visuellen Rhythmus für diese Kampagne. Auto folgt dem natürlichen Stil des Clones aus den Samples.",
    rhythmAutoLabel: "Auto",
    rhythmAutoHint: "Aus Clone-Samples",
    rhythmTightLabel: "Kompakt",
    rhythmTightHint: "Kurze Einzeiler",
    rhythmStandardLabel: "Standard",
    rhythmStandardHint: "Kurze Absätze",
    rhythmAiryLabel: "Luftig",
    rhythmAiryHint: "Großzügige Absätze",

    contactDataTitle: "Kontakt­daten",
    contactDataHint: "Felder, die die KI zur Personalisierung nutzen darf.",

    callContextTitle: "Call Context",
    callContextHint: "Die Mail baut auf dem auf, was in den erfassten Calls und Meetings (Notizen) besprochen wurde.",

    // Sign-off identity + greeting rules (per-template addressing controls)
    signOffIdentityTitle:    "Absender-Identität",
    signOffIdentityHint:     "Wie der Absender unter der Mail erscheint (überschreibt das Workspace-Profil).",
    signOffIdentityAuto:     "Aktiven Absender nutzen",
    signOffIdentityAutoDesc: "Signiert mit dem gerade gewählten Absender-Account.",
    signOffIdentityCustom:   "Eigene Identität",
    signOffIdentityCustomDesc:
      "Signiert immer mit der unten konfigurierten Person — unabhängig vom Absender-Account.",
    signOffIdentityNameLabel: "Signatur-Name",
    signOffIdentityNameHint:
      "Wird wortwörtlich als Signatur unter die Mail gesetzt. Nur Vorname (\"Jan\") oder Vor- + Nachname (\"Jan Schubert\") — Leerzeichen sind erlaubt.",
    signOffIdentityNamePlaceholder: "z.B. Jan Schubert",
    signOffIdentityRoleLabel: "Rolle (optional)",
    signOffIdentityRolePlaceholder: "z.B. Co-Founder",

    greetingTitle:    "Anrede-Regeln",
    greetingHint:     "Fixiere die Anrede, damit die KI sie nicht selbst erfindet.",
    greetingModeAuto: "Voice DNA entscheidet",
    greetingModeAutoDesc:
      "Anrede wird im natürlichen Stil des Clones geöffnet.",
    greetingModeConfigured: "Diese Anreden nutzen",
    greetingModeConfiguredDesc:
      "Genaue Anrede pro Fall festlegen (Entscheider bekannt vs. unbekannt).",
    greetingPersonalizationTitle: "Anrede-Personalisierung",
    greetingPersonalizationHint:
      "Bestimme, wie stark die KI den Eröffnungssatz auf das Unternehmen bezieht — wirkt in beiden Modi oben.",
    greetingCompanyMentionLabel: "Unternehmensname in der Anrede erwähnen",
    greetingCompanyMentionAuto:    "Clone entscheidet",
    greetingCompanyMentionInclude: "Immer erwähnen",
    greetingCompanyMentionExclude: "Nie erwähnen",
    greetingCompanyMentionHint:
      "\"Immer erwähnen\" nutzt die markante Kurzform (z.B. \"Müller Optik\" statt \"Müller Optik GmbH\").",
    greetingContextLevelLabel: "Tiefe des Unternehmenskontexts",
    greetingContextLevelHint:
      "Wie sehr der Einstieg auf Branche / Produkt / Rolle eingeht.",
    greetingContext_minimal_label:  "Minimal",
    greetingContext_minimal_desc:   "Schlichte Anrede, kein Unternehmensbezug.",
    greetingContext_light_label:    "Leicht",
    greetingContext_light_desc:     "Erwähnt das Unternehmen kurz, wenn es passt.",
    greetingContext_grounded_label: "Verankert",
    greetingContext_grounded_desc:  "Eröffnung wird im Unternehmenskontext geerdet.",
    greetingContext_deep_label:     "Tief",
    greetingContext_deep_desc:      "Eröffnung greift konkrete Branche- / Produkt- / Rollen-Signale auf.",

    greetingWithFirstNameLabel: "Wenn wir den Vornamen des Empfängers kennen",
    greetingWithFirstNameHint:
      "Für persönliche Adressen wie dieter@acme.com. Platzhalter: {{firstName}}, {{lastName}}, {{recipientName}}, {{businessName}}, {{businessNameShort}}.",
    greetingWithFirstNamePlaceholder: "z.B. Hey {{firstName}},",
    greetingWithoutFirstNameLabel: "Wenn wir nur eine info@-Adresse haben",
    greetingWithoutFirstNameHint:
      "Für info@, kontakt@ usw. Platzhalter: {{businessName}}, {{businessNameShort}}. Kurzform lässt Rechtsformen (GmbH, AG, Inc., …) automatisch weg.",
    greetingWithoutFirstNamePlaceholder: "z.B. Liebes {{businessNameShort}}-Team,",
    greetingPreviewHeader: "Live-Vorschau",
    greetingPreviewWithName: "Mit Vornamen:",
    greetingPreviewWithoutName: "Generisch / kein Entscheider:",

    enhancementsTitle: "Optionale Erweiterungen",
    enhancementsHint: "Extras zusätzlich zu den Kontaktdaten.",
    enhWebResearchLabel: "Live-Webrecherche",
    enhWebResearchDesc:
      "Frischer Kontext direkt von der Website des Empfängers zum Sendezeitpunkt (Homepage-Signale, aktuelle Beiträge).",
    enhRecipientHistoryLabel: "Vorherige Interaktionen",
    enhRecipientHistoryDesc:
      "Falls wir den Empfänger schon kontaktiert haben, vergangene Touchpoints respektvoll referenzieren.",
    enhSenderContextLabel: "Sender-Kontext",
    enhSenderContextDesc:
      "Rolle und Business-Kontext des Senders einfließen lassen, um Relevanz zu schaffen.",
    factsTitle: "Kampagnen-Fakten",
    factsHint: "Whitelist dessen, was die KI behaupten darf.",
    factsPlaceholder:
      "z.B. 10k+ Nutzer seit 2019, in TechCrunch erwähnt, 14 Tage kostenlos testen",
    anglesTitle: "Angles, die betont werden sollen",
    anglesHint: "Themen, die die KI über die Varianten verteilt.",
    anglesPlaceholder:
      "z.B. ROI in 30 Tagen, keine Integrationen nötig, Empfehlungen aus der Branche",
    extraRulesTitle: "Extra rules",
    extraRulesHint:
      "Freiraum für zusätzliche Muss-Regeln oder Notizen, die die KI zusätzlich zum Brief respektieren soll.",
    extraRulesPlaceholder:
      "z.B. In der ersten Mail niemals Preise nennen · Immer ein 15-Min-Call anbieten · Ton bleibt locker, keine Buzzwords · Q4-Launch nur erwähnen wenn Empfänger im Retail ist.",
    subjectsTitle: "Betreff­zeilen",
    subjectsHint: "Manuell oder pro Empfänger automatisch geschrieben.",
    structuresTitle: "Struktur-Varianten",
    structuresHint: "Strukturen parallel A/B-testen.",
    structureSourceTwin: "Vom CopyTwin kopieren",
    structureSourceCustom: "Eigene Strukturen",
    structureSourceTwinDesc:
      "Die E-Mail übernimmt die Struktur und Länge des CopyTwins, gelernt aus seinen Trainings-Mails. Die Struktur-Varianten unten sind optional.",
    structureSourceCustomDesc:
      "Nutze die Struktur-Varianten unten (kuratierte Frameworks, die du A/B-testen kannst).",
    structureSourceTwinUntrained:
      "Trainiere diesen CopyTwin zuerst (Beispiel-Mails hinzufügen), damit er eine Struktur zum Kopieren hat.",
    structureVariantsOptional: "Im CopyTwin-Modus optional",
    taboosTitle: "Kampagnen-Tabus",
    taboosHint: "Phrasen, die niemals auftauchen dürfen.",
    taboosPlaceholder:
      'z.B. "Rabatt", "dringend", Wettbewerber namentlich',

    testRecipientLabel:        "Test-Empfänger",
    testRecipientHint:         "Generiere gegen einen echten Lead, um das Ergebnis ehrlich zu beurteilen.",
    testRecipientLoading:      "Leads werden geladen…",
    testRecipientNone:         "Keine Leads verfügbar — Beispieldaten werden genutzt",
    testRecipientRoll:         "Zufällig",
    testRecipientChange:       "Empfänger wechseln",
    testRecipientFallback:     "Beispiel (Müller Optik)",
    testRecipientPickFromList: "Aus Liste wählen",
    testRecipientNotContactedOnly: "Nur nicht-kontaktierte Leads",

    adherenceTitle:            "Treue",
    adherenceHint:             "Wie strikt das Modell an Stimme und Brief klebt.",
    voiceAdherenceLabel:       "Voice-Treue",
    voiceAdherenceHint:        "Wie eng die Mail die Stilmuster des Schreibers spiegelt.",
    briefAdherenceLabel:       "Brief-Treue",
    briefAdherenceHint:        "Wie vollständig jeder Brief-Punkt in der Mail auftauchen muss.",
    voice_free_label:          "Frei",
    voice_free_desc:           "Stimme als sanfte Inspiration. Das Modell hat mehr Freiraum für den Empfänger.",
    voice_balanced_label:      "Ausgewogen",
    voice_balanced_desc:       "Voice-Patterns prägen Rhythmus und Wortwahl, ohne jeden Satz zu zwingen.",
    voice_strict_label:        "Strikt",
    voice_strict_desc:         "Die meisten Sätze müssen mindestens ein DNA-Pattern spiegeln.",
    voice_mirror_label:        "Mirror Clone",
    voice_mirror_desc:         "Maximale Voice-Treue — liest sich, als hätte der Clone selbst geschrieben.",
    brief_loose_label:         "Locker",
    brief_loose_desc:          "Brief nur nutzen wenn er die Mail stärkt. Punkte dürfen ausgelassen werden.",
    brief_balanced_label:      "Ausgewogen",
    brief_balanced_desc:       "Wichtigste Brief-Punkte kommen natürlich rein. Nicht jeder muss auftauchen.",
    brief_strict_label:        "Strikt",
    brief_strict_desc:         "Jeder Brief-Punkt (Referenzen, Special, Pain, Goal, Angles) muss reflektiert werden.",
    creativityLabel:           "Kreativität",
    creativityHint:            "Wie stark Wortwahl, Einstieg und Blickwinkel von Mail zu Mail variieren.",
    creativity_precise_label:  "Präzise",
    creativity_precise_desc:   "Sehr konstant. Bleibt eng an bewährten Formulierungen — wenig Varianz.",
    creativity_balanced_label: "Ausgewogen",
    creativity_balanced_desc:  "Gesunde Mischung aus Konstanz und frischer Wortwahl. Guter Standard.",
    creativity_high_label:     "Kreativ",
    creativity_high_desc:      "Mehr Varianz bei Wortwahl, Einstieg und Blickwinkel von Mail zu Mail.",
    creativity_max_label:      "Max",
    creativity_max_desc:       "Maximale Varianz. Jede Mail nimmt einen merklich anderen Ansatz.",

    headerBack:                "Zurück",
    headerNamePlaceholder:     "Unbenannte Kampagne",
    headerType_intro:          "Erstkontakt",
    headerType_follow_up_1:    "Follow-up 1",
    headerType_follow_up_2:    "Follow-up 2",
    headerType_follow_up_3:    "Follow-up 3",
    headerType_custom_reply:   "Custom Reply",
    headerType_default:        "Template",
    headerStatSent:            "Versendet",
    headerStatReplies:         "Antworten",
    headerStatRate:            "Antwortrate",
    headerToggleActive:        "Aktiv im Outreach",
    headerToggleInactive:      "Inaktiv",
    autoSaveSaving:            "Speichert…",
    autoSaveSaved:             "Gespeichert",
    autoSaveDirty:             "Gespeichert",
    autoSaveError:             "Fehler",
    autoSaveDisabled:          "Pausiert",

    dangerTitle: "Template löschen",
    dangerHint:
      "Entfernt die Kampagnen-Konfiguration und Statistiken. Der CopyTwin selbst bleibt unangetastet.",
    dangerBtn: "Template löschen",
    confirmTitle: "Template wirklich löschen?",
    confirmText:
      "Das entfernt die Kampagnen-Konfiguration und alle Varianten-Statistiken. Der CopyTwin selbst bleibt erhalten.",
    confirmCancel: "Abbrechen",
    confirmDelete: "Löschen",
  },
};

const EditorLangContext = createContext("en");

export const useEditorLang = () => useContext(EditorLangContext);

export const useT = () => {
  const lang = useContext(EditorLangContext);
  const dict = EDITOR_TRANSLATIONS[lang] || EDITOR_TRANSLATIONS.en;
  return useCallback(
    (key) => dict[key] ?? EDITOR_TRANSLATIONS.en[key] ?? key,
    [dict],
  );
};

// =============================================================================
// CLONE AVG-WORDS HELPER — derive how many words this clone naturally uses
// per email from its training samples. Used only when a variant's tweak is
// "Mirror clone". Returns null if the clone has no usable samples; callers
// then fall back to the structure's baseWords.
// =============================================================================

const computeCloneAvgEmailWords = (clone) => {
  const emails = clone?.voiceSamples?.fullEmails;
  if (!Array.isArray(emails) || emails.length === 0) return null;
  const counts = emails
    .map((e) => (e?.body || "").trim().split(/\s+/).filter(Boolean).length)
    .filter((c) => c > 0);
  if (counts.length === 0) return null;
  return Math.round(counts.reduce((a, b) => a + b, 0) / counts.length);
};

// =============================================================================
// STRUCTURE BLUEPRINT — visual breakdown of what happens when in an email.
// Used in two places:
//   1. Structure picker cards: shows proportional split (no clone needed,
//      just the structure's natural shape).
//   2. Variant cards: shows the actual word counts after the chosen tweak.
//
// The bar uses BEAT_META colors so the same beat type looks the same across
// every structure (e.g. "pain" is always red, "cta" is always navy).
// =============================================================================

// =============================================================================
// STRUCTURE STACK — vertical mock-up of the email's actual shape.
// Each beat becomes a colored block whose HEIGHT is proportional to its
// word count, so the user literally sees the email's silhouette before
// picking it (long story-led top + small CTA, vs short pain block on top
// of equally short relief, etc.).
//
// Lives inside the structure picker slider; works at any container width.
// =============================================================================

const StructureStack = ({
  structureId,
  tweakId = "natural",
  cloneAvgWords,
  customWords,
}) => {
  const beats = computeBeatBreakdown(structureId, tweakId, cloneAvgWords, customWords);
  if (!beats.length) return null;
  return (
    <div className="vct-stack" role="img" aria-label="Email structure shape">
      {beats.map((b, i) => (
        <div
          key={`${b.id}-${i}`}
          className="vct-stack-block"
          style={{
            flex: b.weight,
            background: b.meta.color,
          }}
          title={`${b.meta.label} — ~${b.words} words${
            b.hint ? ` · ${b.hint}` : ""
          }`}
        >
          <span className="vct-stack-block-label">{b.meta.label}</span>
          <span className="vct-stack-block-words">~{b.words}w</span>
        </div>
      ))}
    </div>
  );
};

const StructureBlueprint = ({
  structureId,
  tweakId = "natural",
  cloneAvgWords,
  customWords,
  showWords = true,
  size = "md",
}) => {
  const beats = computeBeatBreakdown(structureId, tweakId, cloneAvgWords, customWords);
  if (!beats.length) return null;
  const totalWords = beats.reduce((s, b) => s + b.words, 0);

  return (
    <div className={`vct-bp vct-bp-${size}`}>
      <div className="vct-bp-bar" role="img" aria-label="Email structure breakdown">
        {beats.map((b, i) => (
          <div
            key={`${b.id}-${i}`}
            className="vct-bp-bar-seg"
            style={{ flex: b.weight, background: b.meta.color }}
            title={`${b.meta.label} — ${b.pct}%${
              showWords ? ` · ~${b.words} words` : ""
            }${b.hint ? ` · ${b.hint}` : ""}`}
          />
        ))}
      </div>
      <div className="vct-bp-legend">
        {beats.map((b, i) => (
          <div key={`${b.id}-${i}`} className="vct-bp-leg">
            <span
              className="vct-bp-leg-dot"
              style={{ background: b.meta.color }}
            />
            <span className="vct-bp-leg-label">{b.meta.label}</span>
            <span className="vct-bp-leg-meta">
              {showWords ? `~${b.words}w` : `${b.pct}%`}
            </span>
          </div>
        ))}
      </div>
      {showWords && (
        <div className="vct-bp-total">
          <i className="fa-solid fa-text-width" />
          Target: <strong>~{totalWords} words</strong>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// VOICE CLONE TEMPLATE EDITOR
//
// A full-screen editor for templates created in Voice-Clone mode. Replaces
// the standard master editor but writes to the SAME RTDB path so the
// existing list rendering / stats badges / generation pipeline can stay
// in lock-step.
//
// Self-contained: it owns its draft state and persists directly to RTDB.
// =============================================================================

// Template type is no longer editable here — it's already chosen by the
// type-group the user clicked "+ Add New" on. We still display it as a pill
// in the header for clarity, but the type field is fixed at creation.
const TEMPLATE_TYPE_LABELS = {
  intro:        "Introduction Email",
  follow_up_1:  "Follow-up 1",
  follow_up_2:  "Follow-up 2",
  follow_up_3:  "Follow-up 3",
  custom_reply: "Custom Reply",
};

// Quick-fill goal suggestions — the user can pick one as a starting point or
// type their own freeform description. Stored as plain text in cloneConfig.goal.
// Labels and texts are resolved against EDITOR_TRANSLATIONS at render time so
// they automatically follow the campaign language (en/de).
const GOAL_SUGGESTIONS = [
  { id: "reply",   icon: "fa-reply",          labelKey: "goalSugReplyLabel",   textKey: "goalSugReplyText"   },
  { id: "meeting", icon: "fa-calendar-check", labelKey: "goalSugMeetingLabel", textKey: "goalSugMeetingText" },
  { id: "click",   icon: "fa-mouse-pointer",  labelKey: "goalSugClickLabel",   textKey: "goalSugClickText"   },
  { id: "warmup",  icon: "fa-hand-wave",      labelKey: "goalSugWarmupLabel",  textKey: "goalSugWarmupText"  },
];

// --------------------- Reply rate helper ---------------------

const replyRate = (sent, replies) => {
  if (!sent || sent === 0) return 0;
  return ((replies || 0) / sent) * 100;
};

// --------------------- InfoTooltip ----------------------------
// A tiny circled-i button that reveals an explanation on hover/focus.
// Used everywhere instead of inline hint paragraphs so the editor stays
// visually quiet — but the help is still one click/hover away.

const InfoTooltip = ({ text, side = "top" }) => {
  if (!text) return null;
  return (
    <span
      className={`vct-info-tip vct-info-tip-${side}`}
      tabIndex={0}
      role="button"
      aria-label={text}
      data-tip={text}
    >
      <i className="fa-solid fa-circle-info" />
    </span>
  );
};

// --------------------- Subsection wrapper ---------------------
// `hint` renders as a SHORT inline subtitle under the title (one line of
// orientation, ~5–10 words). Anything longer belongs in an InfoTooltip on
// a specific control inside the section, not in the section header.

const Section = ({ icon, title, hint, children, accent = "blue", count }) => (
  <div className={`vct-section vct-section-${accent}`}>
    <div className="vct-section-head">
      <div className="vct-section-icon">
        <i className={`fa-solid ${icon}`} />
      </div>
      <div className="vct-section-meta">
        <h3 className="vct-section-title">
          {title}
          {count != null && <span className="vct-section-count">{count}</span>}
        </h3>
        {hint && <p className="vct-section-hint">{hint}</p>}
      </div>
    </div>
    <div className="vct-section-body">{children}</div>
  </div>
);

// --------------------- Language picker (campaign + UI in one) ---------------------
//
// One control with two effects:
//   1. Sets the language used for the generated emails (passed downstream
//      to the existing email pipeline as `cloneConfig.language`).
//   2. If the chosen language is German or English, the editor's UI strings
//      switch with it (handled at the parent level via EditorLangContext).
//      Other languages → editor stays English; only the email output changes.

const LanguagePicker = ({ value, onChange }) => (
  <div className="vct-lang-grid" role="radiogroup" aria-label="Campaign language">
    {CAMPAIGN_LANGUAGES.map((lang) => (
      <button
        key={lang.value}
        type="button"
        role="radio"
        aria-checked={value === lang.value}
        className={`vct-lang-btn ${value === lang.value ? "active" : ""}`}
        onClick={() => onChange(lang.value)}
        title={lang.value}
      >
        <span className="vct-lang-flag" aria-hidden="true">{lang.flag}</span>
        <span className="vct-lang-code">{lang.code}</span>
        {(lang.uiLang === "en" || lang.uiLang === "de") && (
          <span className="vct-lang-ui" title={`Editor available in ${lang.value}`}>
            UI
          </span>
        )}
      </button>
    ))}
  </div>
);

// --------------------- Paragraph rhythm picker ---
//
// Per-campaign override on top of the clone's natural paragraph style.
// Stored on cloneConfig.paragraphRhythm. The backend uses 'auto' as the
// neutral default that lets the Voice DNA's learned paragraphStyle win;
// any other value pushes a hard styling instruction to the prompt.

const PARAGRAPH_RHYTHMS = [
  { id: "auto",     icon: "fa-wand-magic-sparkles", labelKey: "rhythmAutoLabel",     hintKey: "rhythmAutoHint"     },
  { id: "tight",    icon: "fa-compress",            labelKey: "rhythmTightLabel",    hintKey: "rhythmTightHint"    },
  { id: "standard", icon: "fa-align-left",          labelKey: "rhythmStandardLabel", hintKey: "rhythmStandardHint" },
  { id: "airy",     icon: "fa-expand",              labelKey: "rhythmAiryLabel",     hintKey: "rhythmAiryHint"     },
];

const ParagraphRhythmPicker = ({ value, onChange }) => {
  const t = useT();
  const current = value || "auto";
  return (
    <div
      className="vct-rhythm-grid"
      role="radiogroup"
      aria-label="Paragraph rhythm"
    >
      {PARAGRAPH_RHYTHMS.map((r) => (
        <button
          key={r.id}
          type="button"
          role="radio"
          aria-checked={current === r.id}
          className={`vct-rhythm-btn ${current === r.id ? "active" : ""}`}
          onClick={() => onChange(r.id)}
        >
          <span className="vct-rhythm-icon">
            <i className={`fa-solid ${r.icon}`} />
          </span>
          <span className="vct-rhythm-text">
            <span className="vct-rhythm-label">{t(r.labelKey)}</span>
            <span className="vct-rhythm-hint">{t(r.hintKey)}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

// --------------------- Adherence picker (Voice + Brief dials) -------------------
//
// Generic 2-to-4-step radio grid used twice: once for `voiceAdherence`, once
// for `briefAdherence`. Same visual language as the rhythm picker so the
// Details tab feels cohesive. Each level emits its label + 1-line hint via
// the editor's translation hook so DE/EN stay in sync.

const AdherencePicker = ({ levels, value, onChange, ariaLabel, keyPrefix }) => {
  const t = useT();
  return (
    <div
      className="vct-adherence-grid"
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {levels.map((lvl) => {
        const labelKey = `${keyPrefix}_${lvl.id}_label`;
        const descKey  = `${keyPrefix}_${lvl.id}_desc`;
        return (
          <button
            key={lvl.id}
            type="button"
            role="radio"
            aria-checked={value === lvl.id}
            className={`vct-adherence-btn ${value === lvl.id ? "active" : ""}`}
            onClick={() => onChange(lvl.id)}
            title={t(descKey)}
          >
            <span className="vct-adherence-icon">
              <i className={`fa-solid ${lvl.icon}`} />
            </span>
            <span className="vct-adherence-text">
              <span className="vct-adherence-label">{t(labelKey)}</span>
              <span className="vct-adherence-hint">{t(descKey)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

// --------------------- Editor tabs (Essentials / Personalization / Details) ---
//
// Lean tab pill: icon + label + a single status dot (orange/blue/green)
// that signals at a glance whether a tab still needs love. The exact "X/Y"
// number is one hover away (title attr) — not in the chrome.

const EditorTabs = ({ active, onChange, progress }) => {
  const t = useT();
  const tabs = [
    { id: "essentials",      icon: "fa-bullseye", label: t("tabEssentials")      },
    { id: "personalization", icon: "fa-user-pen", label: t("tabPersonalization") },
    { id: "details",         icon: "fa-sliders",  label: t("tabDetails")         },
  ];
  return (
    <div className="vct-tabs" role="tablist">
      {tabs.map((tab) => {
        const p = progress?.[tab.id];
        const filled = p?.filled ?? 0;
        const total = p?.total ?? 0;
        const showDot = total > 0;
        const complete = showDot && filled >= total;
        const empty = showDot && filled === 0;
        const status = complete ? "complete" : empty ? "empty" : "partial";
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            className={`vct-tab ${active === tab.id ? "active" : ""}`}
            onClick={() => onChange(tab.id)}
            title={showDot ? `${filled} / ${total} filled` : undefined}
          >
            <i className={`fa-solid ${tab.icon} vct-tab-icon`} />
            <span className="vct-tab-label">{tab.label}</span>
            {showDot && (
              <span
                className={`vct-tab-dot is-${status}`}
                aria-label={`${filled} of ${total} filled`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

// --------------------- Campaign Brief — the heart of the campaign ----------------
//
// One Section that consolidates the three answers the AI absolutely needs:
//   WHO  → audience profile
//   WHAT → the offer / pitch core
//   WHY  → the goal / desired outcome
// Together with the clone's Voice DNA (style) this is everything required
// to write maximally relevant emails. Each field has a one-line micro-coach
// describing why it matters, plus rich placeholders that show what "great"
// looks like.

const CampaignBrief = ({ uniqueAdvantage, painPoint, goal, onChange }) => {
  const t = useT();
  return (
    <div className="vct-brief">
      <div className="vct-brief-field">
        <label className="vct-brief-label" htmlFor="vct-brief-special">
          <span className="vct-brief-label-tag">SPECIAL</span>
          {t("briefSpecialLabel")}
          <InfoTooltip text={t("briefSpecialMicro")} />
        </label>
        <textarea
          id="vct-brief-special"
          className="vct-textarea"
          rows={2}
          value={uniqueAdvantage}
          placeholder={t("briefSpecialPlaceholder")}
          onChange={(e) => onChange({ uniqueAdvantage: e.target.value })}
        />
      </div>

      <div className="vct-brief-field">
        <label className="vct-brief-label" htmlFor="vct-brief-pain">
          <span className="vct-brief-label-tag">PAIN</span>
          {t("briefPainLabel")}
          <InfoTooltip text={t("briefPainMicro")} />
        </label>
        <textarea
          id="vct-brief-pain"
          className="vct-textarea"
          rows={2}
          value={painPoint}
          placeholder={t("briefPainPlaceholder")}
          onChange={(e) => onChange({ painPoint: e.target.value })}
        />
      </div>

      <div className="vct-brief-field">
        <label className="vct-brief-label" htmlFor="vct-brief-goal">
          <span className="vct-brief-label-tag">GOAL</span>
          {t("briefGoalLabel")}
          <InfoTooltip text={t("briefGoalMicro")} />
        </label>
        <textarea
          id="vct-brief-goal"
          className="vct-textarea"
          rows={2}
          value={goal}
          placeholder={t("goalPlaceholder")}
          onChange={(e) => onChange({ goal: e.target.value })}
        />
        <div className="vct-brief-quickfill">
          {GOAL_SUGGESTIONS.map((g) => (
            <button
              key={g.id}
              type="button"
              className="vct-goal-suggestion"
              onClick={() => onChange({ goal: t(g.textKey) })}
              title={t(g.textKey)}
            >
              <i className={`fa-solid ${g.icon}`} />
              {t(g.labelKey)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// --------------------- Info Add-ons ----------------------------
// Flexible info pools the user attaches to the template. Each addon has a
// label, an icon, and a list of items (chips). Presets give one-tap
// creation for the most common categories (case studies, testimonials,
// stats, etc.); "custom" lets the user define an arbitrary bucket.
//
// Visually: a row of "+" buttons on top, then the added pools as compact
// cards below — each with a chip-list of items inside. The pools that are
// already added are filtered out of the catalog so the user only sees
// what's still available.

const InfoAddonItemEditor = ({ items, onAdd, onRemove }) => {
  const t = useT();
  const [val, setVal] = useState("");
  const submit = () => {
    const v = val.trim();
    if (!v) return;
    onAdd(v);
    setVal("");
  };
  return (
    <div className="vct-addon-items">
      {items.map((it, i) => (
        <span key={i} className="vct-addon-chip">
          {it}
          <button
            type="button"
            className="vct-addon-chip-x"
            onClick={() => onRemove(i)}
            aria-label="Remove"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </span>
      ))}
      <input
        type="text"
        className="vct-addon-input"
        value={val}
        placeholder={t("addonsItemPlaceholder")}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        onBlur={submit}
      />
    </div>
  );
};

// Inline editable label for custom pools. Plain text by default; click /
// keyboard-focus → text input, blur or Enter commits, Escape reverts.
const EditableAddonLabel = ({ value, editable, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  if (!editable) {
    return <span className="vct-addon-label">{value}</span>;
  }
  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        className="vct-addon-label-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const v = draft.trim();
          if (v && v !== value) onChange(v);
          else setDraft(value);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter")  e.currentTarget.blur();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
      />
    );
  }
  return (
    <button
      type="button"
      className="vct-addon-label vct-addon-label-editable"
      onClick={() => setEditing(true)}
      title="Click to rename"
    >
      {value}
      <i className="fa-solid fa-pen vct-addon-label-pen" />
    </button>
  );
};

const InfoAddons = ({ addons, onChange }) => {
  const t = useT();
  const usedTypes = new Set(addons.map((a) => a.type));
  // "custom" can always be added (multiple custom pools allowed); presets
  // disappear from the catalog once added so the user only sees what's left.
  const available = INFO_ADDON_PRESETS.filter(
    (p) => p.id === "custom" || !usedTypes.has(p.id),
  );

  // null = not in custom-add mode; string = inline input visible with that draft.
  const [customDraft, setCustomDraft] = useState(null);

  const commitAddon = (presetId, label) => {
    const next = createInfoAddon(presetId, label);
    onChange([...addons, next]);
  };

  const handlePresetClick = (presetId) => {
    if (presetId === "custom") {
      setCustomDraft("");
      return;
    }
    commitAddon(presetId);
  };

  const submitCustom = () => {
    const v = (customDraft || "").trim();
    if (v) commitAddon("custom", v);
    setCustomDraft(null);
  };

  const updateAddon = (id, patch) =>
    onChange(addons.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const removeAddon = (id) =>
    onChange(addons.filter((a) => a.id !== id));

  return (
    <div className="vct-addons">
      <div className="vct-addons-catalog">
        {available.map((p) =>
          p.id === "custom" && customDraft !== null ? (
            <span key="custom-input" className="vct-addon-add-inline">
              <i className={`fa-solid ${p.icon}`} />
              <input
                autoFocus
                type="text"
                className="vct-addon-add-input"
                value={customDraft}
                placeholder={t("addonsCustomPlaceholder")}
                onChange={(e) => setCustomDraft(e.target.value)}
                onBlur={submitCustom}
                onKeyDown={(e) => {
                  if (e.key === "Enter")  e.currentTarget.blur();
                  if (e.key === "Escape") setCustomDraft(null);
                }}
              />
            </span>
          ) : (
            <button
              key={p.id}
              type="button"
              className={`vct-addon-add ${p.id === "custom" ? "is-custom" : ""}`}
              onClick={() => handlePresetClick(p.id)}
            >
              <i className={`fa-solid ${p.icon}`} />
              {p.id === "custom"
                ? t("addonsCustomLabel")
                : t(`addonPreset_${p.id}`)}
            </button>
          ),
        )}
      </div>

      {addons.length > 0 && (
        <div className="vct-addons-list">
          {addons.map((a) => {
            const preset = INFO_ADDON_PRESET_BY_ID[a.type] || INFO_ADDON_PRESET_BY_ID.custom;
            const isCustom = a.type === "custom";
            // Preset pools (non-custom) always render the localized label so
            // the UI follows the editor language, even if the persisted
            // a.label is the English baseline from when it was created.
            // Custom pools keep their user-typed label as the source of truth.
            const displayLabel = isCustom
              ? a.label
              : t(`addonPreset_${a.type}`);
            return (
              <div key={a.id} className="vct-addon-card">
                <div className="vct-addon-head">
                  <span className="vct-addon-icon">
                    <i className={`fa-solid ${a.icon || preset.icon}`} />
                  </span>
                  <EditableAddonLabel
                    value={displayLabel}
                    editable={isCustom}
                    onChange={(label) => updateAddon(a.id, { label })}
                  />
                  <span className="vct-addon-count">{(a.items || []).length}</span>
                  <button
                    type="button"
                    className="vct-addon-remove"
                    onClick={() => removeAddon(a.id)}
                    title={t("addonsRemoveTitle")}
                    aria-label={t("addonsRemoveTitle")}
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                <InfoAddonItemEditor
                  items={a.items || []}
                  onAdd={(v) =>
                    updateAddon(a.id, { items: [...(a.items || []), v] })
                  }
                  onRemove={(i) =>
                    updateAddon(a.id, {
                      items: (a.items || []).filter((_, idx) => idx !== i),
                    })
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// --------------------- Clone selector card ---------------------

const CloneCard = ({ clone, selected, onSelect }) => {
  const samples =
    (clone.voiceSamples?.fullEmails?.length || 0) +
    (clone.voiceSamples?.subjectLines?.length || 0);
  const trained = !!clone.voiceDNA;
  const status = clone.status || "draft";

  return (
    <button
      type="button"
      className={`vct-clone-card ${selected ? "active" : ""}`}
      onClick={() => onSelect(clone.id)}
    >
      <div className="vct-clone-card-icon">
        <i className="fa-solid fa-fingerprint" />
      </div>
      <div className="vct-clone-card-body">
        <div className="vct-clone-card-name">
          {clone.name || "Untitled clone"}
        </div>
        <div className="vct-clone-card-meta">
          <span className={`vct-clone-card-status status-${status}`}>
            {status}
          </span>
          <span className="vct-clone-card-stat">{samples} samples</span>
          {trained && (
            <span className="vct-clone-card-trained">
              <i className="fa-solid fa-check" /> trained
            </span>
          )}
        </div>
      </div>
      {selected && (
        <div className="vct-clone-card-check">
          <i className="fa-solid fa-check" />
        </div>
      )}
    </button>
  );
};

// --------------------- Clone voice description (inline editor) ---------------------
// The clone's `identity.story` is the top-priority voice anchor in the prompt,
// but the only place to edit it used to be buried behind a collapsed toggle in
// the separate clone editor — so when the "Clone description is empty" warning
// fired here, users had nowhere obvious to act. This inline field lets them fill
// it in right where the warning lives. It writes straight to the CLONE (shared
// across every campaign that uses it), debounced so we don't hammer RTDB.
const CloneVoiceDescriptionField = ({ userId, clone }) => {
  const t = useT();
  const cloneId = clone?.id;
  const serverStory = clone?.identity?.story || "";
  const [value, setValue] = useState(serverStory);
  const [savedTick, setSavedTick] = useState(false);
  const saveTimer = useRef(null);
  const lastSaved = useRef(serverStory);

  // Re-sync when switching to a different clone (or when an external edit lands
  // and the user isn't mid-typing on the old value).
  useEffect(() => {
    setValue(serverStory);
    lastSaved.current = serverStory;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneId]);

  const fingerprint = (clone?.voiceDNA?.voiceFingerprint || "").trim();
  const suggestion = fingerprint
    ? fingerprint.split(/(?<=[.!?])\s+/).slice(0, 2).join(" ").trim()
    : "";

  const persist = useCallback(
    (next) => {
      if (!userId || !cloneId) return;
      if (next === lastSaved.current) return;
      lastSaved.current = next;
      updateClone(userId, cloneId, {
        identity: { ...(clone?.identity || {}), story: next },
      })
        .then(() => {
          setSavedTick(true);
          setTimeout(() => setSavedTick(false), 1800);
        })
        .catch(() => {});
    },
    [userId, cloneId, clone],
  );

  const onChange = (next) => {
    setValue(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next.trim()), 700);
  };

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  return (
    <div className="vct-clone-desc">
      <div className="vct-clone-desc-label">
        <i className="fa-solid fa-pen-fancy" />
        {t("voiceDescTitle")}
      </div>
      <p className="vct-clone-desc-hint">{t("voiceDescHint")}</p>
      <textarea
        className="vct-clone-desc-input"
        rows={3}
        value={value}
        placeholder={suggestion || t("voiceDescPlaceholder")}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          clearTimeout(saveTimer.current);
          persist(value.trim());
        }}
      />
      <div className="vct-clone-desc-foot">
        {!value.trim() && suggestion && (
          <button
            type="button"
            className="vct-clone-desc-autofill"
            onClick={() => onChange(suggestion)}
          >
            <i className="fa-solid fa-wand-magic-sparkles" />
            {t("voiceDescAutofill")}
          </button>
        )}
        {savedTick && (
          <span className="vct-clone-desc-saved">
            <i className="fa-solid fa-circle-check" /> {t("voiceDescSavedToClone")}
          </span>
        )}
      </div>
    </div>
  );
};

// --------------------- Chip list (for facts / angles / taboos) ---------------------

const ChipList = ({ items, onAdd, onRemove, placeholder, accent = "blue" }) => {
  const [val, setVal] = useState("");
  const submit = () => {
    const v = val.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    onAdd(v);
    setVal("");
  };
  return (
    <div className={`vct-chiplist vct-chiplist-${accent}`}>
      {items.map((item, i) => (
        <span key={`${item}-${i}`} className="vct-chip">
          {item}
          <button type="button" onClick={() => onRemove(i)} aria-label="Remove">
            <i className="fa-solid fa-xmark" />
          </button>
        </span>
      ))}
      <input
        className="vct-chip-input"
        value={val}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        onBlur={submit}
      />
    </div>
  );
};

// --------------------- Structure slider (center-stage carousel) ---------------------
//
// Two distinct states:
//   - CENTERED (activeIndex)  → the card you're currently looking at;
//                                local-only, doesn't commit anything.
//   - SELECTED (selectedId)   → the structure committed for this variant;
//                                lives on the parent. ONLY changes when
//                                the user explicitly clicks the centered
//                                card.
// Browsing (arrows / dots / clicking a side card) just changes the centered
// card. This keeps "look around" cheap and "select" a deliberate gesture.

// Card width + gap drive the centering math. Kept in sync with the CSS
// (.vct-stack-card width + .vct-stage-track gap).
const STACK_CARD_WIDTH = 260;
const STACK_CARD_GAP   = 18;

// Length buckets for the size filter above the carousel.
const STRUCTURE_LENGTH_FILTERS = [
  { id: "all",    label: "All",    test: () => true },
  { id: "short",  label: "Short",  hint: "≤ 80 words",       test: (s) => s.baseWords <= 80 },
  { id: "medium", label: "Medium", hint: "81 – 150 words",   test: (s) => s.baseWords > 80  && s.baseWords <= 150 },
  { id: "long",   label: "Long",   hint: "151+ words",       test: (s) => s.baseWords > 150 },
];

const StructureSlider = ({ structures, selectedId, onSelect }) => {
  const [lengthFilter, setLengthFilter] = useState("all");

  // Apply length filter — show only structures matching the bucket.
  const filtered = useMemo(() => {
    const f = STRUCTURE_LENGTH_FILTERS.find((x) => x.id === lengthFilter);
    return structures.filter(f?.test || (() => true));
  }, [structures, lengthFilter]);

  // Counts per bucket for the filter pills.
  const filterCounts = useMemo(() => {
    const counts = {};
    STRUCTURE_LENGTH_FILTERS.forEach((f) => {
      counts[f.id] = structures.filter(f.test).length;
    });
    return counts;
  }, [structures]);

  // Active = currently centered. Default to selected if visible, else 0.
  const initialIdx = Math.max(
    0,
    filtered.findIndex((s) => s.id === selectedId),
  );
  const [activeIndex, setActiveIndex] = useState(initialIdx);

  // When filter changes, snap to the selected (if still in the filtered set)
  // or to the first card. Keeps the user oriented after switching buckets.
  useEffect(() => {
    const idx = filtered.findIndex((s) => s.id === selectedId);
    setActiveIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lengthFilter]);

  // Sync local active when parent selection changes externally (e.g. when a
  // variant is cloned and re-opens the picker on a specific structure). Only
  // jumps if the selected structure is actually visible under the current
  // filter.
  useEffect(() => {
    if (!selectedId) return;
    const idx = filtered.findIndex((s) => s.id === selectedId);
    if (idx >= 0 && idx !== activeIndex) setActiveIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Browse-only navigation — does NOT commit a selection.
  const goTo = (idx) => {
    const clamped = Math.max(0, Math.min(filtered.length - 1, idx));
    setActiveIndex(clamped);
  };

  // Click on a card → if it's the centered one, COMMIT the selection;
  // otherwise just bring it to center (browse).
  const handleCardClick = (idx) => {
    if (idx === activeIndex) {
      onSelect(filtered[idx].id);
    } else {
      goTo(idx);
    }
  };

  // Centering math: shift the track so the active card's CENTER lines up
  // with the WINDOW's center.
  //
  // ⚠️ We can NOT use `translateX(calc(50% - ...))` here, because a CSS
  // percentage on `transform` resolves against the element's OWN width
  // (the track is ~17 cards wide ≈ 4700px), not the window. That made the
  // active card drift further off-center with every step. So we measure
  // the window width via ResizeObserver and translate by an absolute px
  // value instead.
  const windowRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(0);

  useLayoutEffect(() => {
    const el = windowRef.current;
    if (!el) return;
    const update = () => setWindowWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const trackOffsetPx = windowWidth
    ? windowWidth / 2 -
      (activeIndex * (STACK_CARD_WIDTH + STACK_CARD_GAP) + STACK_CARD_WIDTH / 2)
    : 0;

  return (
    <div className="vct-stage">
      {/* Length filter — lets the user browse by structure size first */}
      <div className="vct-stage-filters" role="tablist" aria-label="Structure length">
        {STRUCTURE_LENGTH_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={lengthFilter === f.id}
            className={`vct-stage-filter ${lengthFilter === f.id ? "active" : ""}`}
            onClick={() => setLengthFilter(f.id)}
            title={f.hint || f.label}
            disabled={filterCounts[f.id] === 0}
          >
            {f.label}
            <span className="vct-stage-filter-count">{filterCounts[f.id]}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="vct-stage-nav vct-stage-nav-prev"
        onClick={() => goTo(activeIndex - 1)}
        disabled={activeIndex === 0}
        aria-label="Previous structure"
      >
        <i className="fa-solid fa-chevron-left" />
      </button>

      <div className="vct-stage-window" ref={windowRef}>
        <div
          className="vct-stage-track"
          style={{ transform: `translateX(${trackOffsetPx}px)` }}
        >
          {filtered.map((s, i) => {
            const absDist = Math.abs(i - activeIndex);
            const isCentered = absDist === 0;
            const isSelected = s.id === selectedId;
            // Visual state class: centered + selected stack independently.
            const stateClass = `${
              isCentered ? "is-centered" : absDist === 1 ? "is-near" : "is-far"
            } ${isSelected ? "is-selected" : ""}`;
            return (
              <button
                key={s.id}
                type="button"
                className={`vct-stack-card ${stateClass}`}
                onClick={() => handleCardClick(i)}
                data-accent={s.accent}
                aria-current={isCentered}
                aria-pressed={isSelected}
                tabIndex={isCentered ? 0 : -1}
                title={
                  isCentered
                    ? isSelected
                      ? "Selected"
                      : "Click to select this structure"
                    : `Click to view ${s.label}`
                }
              >
                {isSelected && (
                  <div className="vct-stack-card-check" aria-hidden="true">
                    <i className="fa-solid fa-check" />
                  </div>
                )}

                <div className="vct-stack-card-head">
                  <div className="vct-stack-card-icon">
                    <i className={`fa-solid ${s.icon}`} />
                  </div>
                  <div className="vct-stack-card-title">{s.label}</div>
                  <div
                    className="vct-stack-card-words"
                    title="Natural length for this structure"
                  >
                    ~{s.baseWords}w
                  </div>
                </div>

                <StructureStack structureId={s.id} tweakId="natural" />

                <div className="vct-stack-card-foot">
                  <i className="fa-solid fa-bullseye" />
                  <span>{s.bestFor}</span>
                </div>

                {/* Subtle "Click to select" affordance — only on the
                    centered, not-yet-selected card. Disappears on selection. */}
                {isCentered && !isSelected && (
                  <div className="vct-stack-card-cta">
                    <i className="fa-regular fa-square-check" />
                    Click to select
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className="vct-stage-nav vct-stage-nav-next"
        onClick={() => goTo(activeIndex + 1)}
        disabled={activeIndex === filtered.length - 1}
        aria-label="Next structure"
      >
        <i className="fa-solid fa-chevron-right" />
      </button>

      {/* Position dots — tappable indicator of where you are in the rail. */}
      <div className="vct-stage-dots" role="tablist" aria-label="Structure position">
        {filtered.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={i === activeIndex}
            className={`vct-stage-dot ${i === activeIndex ? "active" : ""} ${
              s.id === selectedId ? "is-selected" : ""
            }`}
            onClick={() => goTo(i)}
            title={s.label}
          />
        ))}
      </div>
    </div>
  );
};

// --------------------- Custom-words inline editor ---------------------
//
// Tiny controlled number field used wherever the user picks the "Custom"
// length tweak — both inside the picker (Step 2) and inline on an existing
// variant card. Buffers the user's keystrokes locally so they can fully
// retype the number; commits only on blur / Enter / +/- chips, and always
// through `sanitizeCustomWords` so the stored value stays inside the safe
// [LENGTH_HARD_FLOOR, LENGTH_HARD_CEILING] range.
//
// Kept dumb on purpose: no knowledge of variants, structure ids, or where
// the value lives — the parent decides what to do with the committed
// number via `onCommit`.
// =============================================================================

const CUSTOM_WORDS_PRESETS = [150, 250, 350, 500];

const CustomWordsField = ({ value, onCommit }) => {
  const safe = sanitizeCustomWords(value);
  const [draft, setDraft] = useState(String(safe));

  // Keep the input in sync if the parent value changes externally (e.g.
  // when the user clicks a +/- preset chip or another control updates it).
  useEffect(() => {
    setDraft(String(safe));
  }, [safe]);

  const commit = (raw) => {
    const next = sanitizeCustomWords(raw);
    setDraft(String(next));
    if (next !== safe) onCommit(next);
  };

  return (
    <div className="vct-custom-words">
      <div className="vct-custom-words-head">
        <i className="fa-solid fa-text-width" />
        <span className="vct-custom-words-title">Target word count</span>
        <span className="vct-custom-words-hint">
          Between {LENGTH_HARD_FLOOR} and {LENGTH_HARD_CEILING} words
        </span>
      </div>
      <div className="vct-custom-words-row">
        <input
          type="number"
          inputMode="numeric"
          min={LENGTH_HARD_FLOOR}
          max={LENGTH_HARD_CEILING}
          step={10}
          className="vct-custom-words-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(e.currentTarget.value);
              e.currentTarget.blur();
            }
          }}
          aria-label="Custom target word count"
        />
        <span className="vct-custom-words-unit">words</span>
        <div className="vct-custom-words-presets">
          {CUSTOM_WORDS_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className={`vct-custom-words-chip ${safe === p ? "active" : ""}`}
              onClick={() => commit(p)}
              title={`Set to ~${p} words`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// --------------------- Clone × template compatibility warnings ---------------------
//
// Pure presentational component for the yellow/red "this setting fights
// your clone" callouts. Used both in variant cards (per-variant length
// conflicts) and at the top of the editor (template-level conflicts:
// paragraph rhythm, greeting override, sign-off identity, etc.). All
// detection logic lives in cloneTemplateUtils (`computeVariantConflicts`
// / `computeTemplateConflicts`) so the same checks can later be run in
// the backend to gate a send or surface a server-side warning.
const SEVERITY_META = {
  high:   { icon: 'fa-triangle-exclamation', label: 'Conflict'  },
  medium: { icon: 'fa-circle-exclamation',   label: 'Warning'   },
  low:    { icon: 'fa-circle-info',          label: 'Heads-up'  },
};

const ConflictChips = ({ conflicts, compact = false }) => {
  if (!Array.isArray(conflicts) || conflicts.length === 0) return null;
  return (
    <div className={`vct-conflicts ${compact ? "vct-conflicts-compact" : ""}`}>
      {conflicts.map((c) => {
        const meta = SEVERITY_META[c.severity] || SEVERITY_META.medium;
        return (
          <div
            key={c.id}
            className={`vct-conflict vct-conflict-${c.severity}`}
            title={c.hint || ''}
          >
            <i className={`fa-solid ${meta.icon} vct-conflict-icon`} />
            <div className="vct-conflict-body">
              <div className="vct-conflict-title">{c.title}</div>
              <div className="vct-conflict-msg">{c.message}</div>
              {c.hint && (
                <div className="vct-conflict-hint">
                  <i className="fa-solid fa-lightbulb" />
                  <span>{c.hint}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Subtle, inline variant of the conflict callout. Rendered directly under the
// control it relates to (filtered by `field`) so the warning lands exactly
// where it's actionable — no big top-of-editor banner. Quiet by default, with
// the fix-hint revealed on hover to keep the UI calm.
const InlineConflict = ({ conflicts, field }) => {
  const list = Array.isArray(conflicts)
    ? conflicts.filter((c) => c.field === field)
    : [];
  if (list.length === 0) return null;
  return (
    <div className="vct-inline-conflicts">
      {list.map((c) => {
        const meta = SEVERITY_META[c.severity] || SEVERITY_META.medium;
        return (
          <div
            key={c.id}
            className={`vct-inline-conflict vct-inline-conflict-${c.severity}`}
          >
            <i className={`fa-solid ${meta.icon} vct-inline-conflict-icon`} />
            <div className="vct-inline-conflict-body">
              <span className="vct-inline-conflict-msg">{c.message}</span>
              {c.hint && (
                <span className="vct-inline-conflict-hint">{c.hint}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --------------------- Variant builder + grid ---------------------

// =============================================================================
// StructureSourceToggle — the default switch at the top of the Structure
// variants section: copy the structure from the CopyTwin (the writer's own
// architecture + length, learned from training emails) OR use custom A/B
// structure variants. Twin is the default; in twin mode the variant list
// below is optional.
// =============================================================================
const StructureSourceToggle = ({ value, onChange, cloneIsTrained, t }) => {
  const isTwin = value === STRUCTURE_SOURCES.TWIN;
  return (
    <div className="vct-structsrc">
      <div className="vct-structsrc-row" role="radiogroup" aria-label="Structure source">
        <button
          type="button"
          role="radio"
          aria-checked={isTwin}
          className={`vct-structsrc-btn ${isTwin ? "active" : ""}`}
          onClick={() => onChange(STRUCTURE_SOURCES.TWIN)}
        >
          <i className="fa-solid fa-clone" />
          {t("structureSourceTwin")}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!isTwin}
          className={`vct-structsrc-btn ${!isTwin ? "active" : ""}`}
          onClick={() => onChange(STRUCTURE_SOURCES.CUSTOM)}
        >
          <i className="fa-solid fa-sliders" />
          {t("structureSourceCustom")}
        </button>
      </div>
      <div className="vct-structsrc-desc">
        <i className="fa-solid fa-circle-info" />
        {isTwin ? t("structureSourceTwinDesc") : t("structureSourceCustomDesc")}
      </div>
      {isTwin && !cloneIsTrained && (
        <div className="vct-structsrc-warn">
          <i className="fa-solid fa-triangle-exclamation" />
          {t("structureSourceTwinUntrained")}
        </div>
      )}
    </div>
  );
};

const VariantBuilder = ({
  variants,
  onAdd,
  onRemove,
  onToggleActive,
  onClone,
  onChangeLength,
  stats,
  cloneAvgWords,
  cloneIsTrained,
  clone,
  optional = false,
  optionalLabel = "",
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickStructure, setPickStructure] = useState(null);
  const [pickTweak, setPickTweak] = useState("natural");
  // Free-form target word count, only consumed when `pickTweak === "custom"`.
  // Seeded with the same default the backend uses so the preview shows a
  // meaningful number the moment the user clicks Custom for the first time.
  const [pickCustomWords, setPickCustomWords] = useState(CUSTOM_WORDS_DEFAULT);
  // Which existing variant has its length-tweak picker open inline. Only one
  // at a time so the grid never gets visually overloaded with editors.
  const [editingLengthId, setEditingLengthId] = useState(null);

  // Reset all picker state in one place — used by both Add and Cancel/X so
  // re-opening the picker always starts from a clean slate (no leaked tweak
  // selection or stale custom word count from a previous session).
  const resetPicker = () => {
    setPickStructure(null);
    setPickTweak("natural");
    setPickCustomWords(CUSTOM_WORDS_DEFAULT);
    setPickerOpen(false);
  };

  const handleAdd = () => {
    if (!pickStructure || !pickTweak) return;
    const v = createVariant(
      pickStructure,
      pickTweak,
      pickTweak === "custom" ? pickCustomWords : undefined,
    );
    if (!v) return;
    onAdd(v);
    resetPicker();
  };

  const totalSent = variants.reduce(
    (acc, v) => acc + (stats?.[v.id]?.sent || 0),
    0,
  );

  // Preview math for the picker step 2 (so the user sees the actual word
  // count for the structure × tweak combo before committing).
  const pickedStructure = pickStructure
    ? STRUCTURE_BY_ID[pickStructure]
    : null;
  const previewWords = pickedStructure
    ? computeTargetWords(
        pickedStructure,
        pickTweak,
        cloneAvgWords,
        pickCustomWords,
      )
    : 0;

  // Loud blocker copy: zero ACTIVE variants means the backend rejects every
  // real generation (NO_ACTIVE_VARIANT). Covers BOTH "no variants at all" and
  // "all variants paused" — the latter is the silent trap the empty state
  // below does not catch.
  const activeVariantCount = variants.filter((v) => v.active).length;

  return (
    <div className={`vct-variants ${optional ? "vct-variants-optional" : ""}`}>
      {/* In CopyTwin mode the structure comes from the twin, so the variant
          list is optional — make that explicit and skip the "must be active"
          blocker (it does not apply here). */}
      {optional && (
        <div className="vct-variants-optional-note">
          <i className="fa-solid fa-circle-info" />
          {optionalLabel || "Optional"}
        </div>
      )}

      {!optional && variants.length > 0 && activeVariantCount === 0 && (
        <div className="vct-variants-blocker" role="alert">
          <i className="fa-solid fa-triangle-exclamation" />
          <div>
            <div className="vct-variants-blocker-title">
              Attention — all variants are paused
            </div>
            <div className="vct-variants-blocker-text">
              At least one variant must be active or emails cannot be generated.
              Click a variant's toggle below to activate it.
            </div>
          </div>
        </div>
      )}

      {variants.length === 0 && !pickerOpen && (
        <div className={`vct-variants-empty ${optional ? "is-optional" : ""}`}>
          <div className="vct-variants-empty-icon">
            <i className="fa-solid fa-flask" />
          </div>
          {optional ? (
            <>
              <div className="vct-variants-empty-title">
                No variants needed
              </div>
              <div className="vct-variants-empty-hint">
                In CopyTwin mode the email already follows your twin's own
                structure and length — nothing to set up here. Adding a variant
                is purely optional, only if you want to A/B test an alternative
                framework side by side (reply rate is tracked per variant).
              </div>
            </>
          ) : (
            <>
              <div className="vct-variants-empty-title">No variants yet</div>
              <div className="vct-variants-empty-hint">
                <strong>Attention:</strong> at least one active variant is
                required — without one, emails cannot be generated. Add a
                structure to start; add multiple to A/B test in parallel (reply
                rate is tracked per variant).
              </div>
            </>
          )}
        </div>
      )}

      {variants.length > 0 && (
        <div className="vct-variant-grid">
          {variants.map((v) => {
            const struct = STRUCTURE_BY_ID[v.structureId] || {};
            // Backwards-compat: old variants stored absolute `lengthId`; new
            // ones store `lengthTweakId`. Resolver handles both.
            const tweakId = resolveLengthTweakId(
              v.lengthTweakId || v.lengthId,
            );
            const tweak = LENGTH_TWEAK_BY_ID[tweakId] || {};
            const variantCustomWords =
              tweak.multiplier === "custom"
                ? sanitizeCustomWords(v.customWords)
                : undefined;
            const vstats = stats?.[v.id] || {};
            const rate = replyRate(vstats.sent, vstats.replies);
            const share =
              totalSent > 0 ? ((vstats.sent || 0) / totalSent) * 100 : 0;

            return (
              <div
                key={v.id}
                className={`vct-variant ${v.active ? "active" : "inactive"}`}
                data-accent={struct.accent || "blue"}
              >
                <div className="vct-variant-head">
                  <div className="vct-variant-icon">
                    <i className={`fa-solid ${struct.icon || "fa-circle"}`} />
                  </div>
                  <div className="vct-variant-titles">
                    <div className="vct-variant-title">{struct.label}</div>
                    {/* Length tweak is editable inline — clicking the chip
                        toggles a compact picker below the head. Falls back
                        to a plain div when no edit handler is wired. */}
                    {onChangeLength ? (
                      <button
                        type="button"
                        className={`vct-variant-subtitle vct-variant-subtitle-btn ${
                          editingLengthId === v.id ? "active" : ""
                        }`}
                        onClick={() =>
                          setEditingLengthId((cur) =>
                            cur === v.id ? null : v.id,
                          )
                        }
                        title="Change length"
                        aria-expanded={editingLengthId === v.id}
                      >
                        <i className={`fa-solid ${tweak.icon || "fa-equals"}`} />
                        {tweak.label || "Natural"}
                        {tweak.multiplier === "custom" && (
                          <span className="vct-variant-subtitle-meta">
                            ~{variantCustomWords}w
                          </span>
                        )}
                        <i
                          className={`fa-solid fa-chevron-${
                            editingLengthId === v.id ? "up" : "down"
                          } vct-variant-subtitle-caret`}
                        />
                      </button>
                    ) : (
                      <div className="vct-variant-subtitle">
                        <i className={`fa-solid ${tweak.icon || "fa-equals"}`} />
                        {tweak.label || "Natural"}
                        {tweak.multiplier === "custom" && (
                          <span className="vct-variant-subtitle-meta">
                            ~{variantCustomWords}w
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="vct-variant-toggle"
                    onClick={() => onToggleActive(v.id)}
                    title={v.active ? "Pause variant" : "Activate variant"}
                  >
                    <i
                      className={`fa-solid ${v.active ? "fa-toggle-on" : "fa-toggle-off"}`}
                    />
                  </button>
                </div>

                {/* Inline length editor — same options as the picker's step 2
                    but compacted: icon + label + word preview, no description.
                    Clicking a tweak commits immediately and closes the row,
                    EXCEPT for "Custom": that one stays open so the user can
                    dial in a precise word count via the inline number input. */}
                {onChangeLength && editingLengthId === v.id && (
                  <>
                    <div
                      className="vct-variant-tweak-row"
                      role="radiogroup"
                      aria-label="Adjust length"
                    >
                      {LENGTH_TWEAKS.map((t) => {
                        const isClone = t.multiplier === "clone";
                        const isCustomTweak = t.multiplier === "custom";
                        const disabled = isClone && !cloneIsTrained;
                        const isActive = t.id === tweakId;
                        // Use the variant's stored customWords when previewing
                        // the Custom chip (or the existing value if the user
                        // is already on Custom), otherwise fall back to the
                        // default so the chip shows a meaningful number.
                        const previewW = computeTargetWords(
                          struct,
                          t.id,
                          cloneAvgWords,
                          isCustomTweak
                            ? variantCustomWords ?? v.customWords
                            : undefined,
                        );
                        return (
                          <button
                            key={t.id}
                            type="button"
                            role="radio"
                            aria-checked={isActive}
                            className={`vct-variant-tweak ${
                              isActive ? "active" : ""
                            } ${disabled ? "disabled" : ""}`}
                            onClick={() => {
                              if (disabled) return;
                              if (isCustomTweak) {
                                // Switch into custom WITHOUT closing the
                                // editor so the user can immediately tweak
                                // the number input that appears below.
                                onChangeLength(v.id, t.id, {
                                  customWords: sanitizeCustomWords(
                                    v.customWords,
                                  ),
                                });
                                return;
                              }
                              onChangeLength(v.id, t.id);
                              setEditingLengthId(null);
                            }}
                            disabled={disabled}
                            title={
                              disabled
                                ? "Train your clone first to mirror its natural length."
                                : t.description
                            }
                          >
                            <i className={`fa-solid ${t.icon}`} />
                            <span className="vct-variant-tweak-label">
                              {t.label}
                            </span>
                            <span className="vct-variant-tweak-meta">
                              {disabled
                                ? "needs trained clone"
                                : `~${previewW}w`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {tweak.multiplier === "custom" && (
                      <CustomWordsField
                        value={variantCustomWords}
                        onCommit={(words) =>
                          onChangeLength(v.id, tweakId, {
                            customWords: words,
                          })
                        }
                      />
                    )}
                  </>
                )}

                {/* Visual blueprint: bar + legend + total word count */}
                <StructureBlueprint
                  structureId={v.structureId}
                  tweakId={tweakId}
                  cloneAvgWords={cloneAvgWords}
                  customWords={variantCustomWords}
                  showWords
                  size="sm"
                />

                {/* Per-variant compatibility warnings — flagged when the
                    variant's length × structure pulls noticeably away from
                    the clone's natural rhythm. Detection lives in
                    cloneTemplateUtils so the same logic can later run
                    server-side. */}
                <ConflictChips
                  conflicts={computeVariantConflicts(clone, v, cloneAvgWords)}
                  compact
                />

                <div className="vct-variant-stats">
                  <div className="vct-variant-stat">
                    <div className="vct-variant-stat-num">
                      {(vstats.sent || 0).toLocaleString()}
                    </div>
                    <div className="vct-variant-stat-label">Sent</div>
                  </div>
                  <div className="vct-variant-stat">
                    <div className="vct-variant-stat-num">
                      {(vstats.replies || 0).toLocaleString()}
                    </div>
                    <div className="vct-variant-stat-label">Replies</div>
                  </div>
                  <div className="vct-variant-stat vct-variant-stat-rate">
                    <div className="vct-variant-stat-num">
                      {rate.toFixed(1)}%
                    </div>
                    <div className="vct-variant-stat-label">Reply rate</div>
                  </div>
                </div>

                {totalSent > 0 && (
                  <div className="vct-variant-share">
                    <div
                      className="vct-variant-share-fill"
                      style={{ width: `${share}%` }}
                    />
                    <span className="vct-variant-share-label">
                      {share.toFixed(0)}% of campaign volume
                    </span>
                  </div>
                )}

                <div className="vct-variant-actions">
                  <button
                    type="button"
                    className="vct-variant-action"
                    onClick={() => onClone(v)}
                    title="Duplicate as new variant"
                  >
                    <i className="fa-solid fa-clone" />
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="vct-variant-action vct-variant-action-danger"
                    onClick={() => onRemove(v.id)}
                    title="Remove variant"
                  >
                    <i className="fa-regular fa-trash-can" />
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pickerOpen ? (
        <div className="vct-picker">
          <div className="vct-picker-head">
            <div className="vct-picker-step">
              <span className="vct-picker-step-num">1</span>
              <span>Choose a structure</span>
            </div>
            <button
              type="button"
              className="vct-picker-close"
              onClick={resetPicker}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {/* Horizontal slider — each card shows the structure as a vertical
              email-shaped stack. The shape itself communicates the rhythm:
              tall block at top + small CTA = top-heavy story, small pain +
              equal relief = balanced, etc. */}
          <StructureSlider
            structures={STRUCTURE_LIBRARY}
            selectedId={pickStructure}
            onSelect={setPickStructure}
          />

          {pickStructure && (
            <>
              <div className="vct-picker-step vct-picker-step-2">
                <span className="vct-picker-step-num">2</span>
                <span>Tweak the length</span>
                <span className="vct-picker-step-meta">
                  Each structure has a natural length built in. You can only
                  nudge it — anything more would break the structure.
                </span>
              </div>
              <div className="vct-tweak-row">
                {LENGTH_TWEAKS.map((t) => {
                  const isClone = t.multiplier === "clone";
                  const isCustomTweak = t.multiplier === "custom";
                  const disabled = isClone && !cloneIsTrained;
                  const previewW = pickedStructure
                    ? computeTargetWords(
                        pickedStructure,
                        t.id,
                        cloneAvgWords,
                        isCustomTweak ? pickCustomWords : undefined,
                      )
                    : 0;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`vct-tweak ${pickTweak === t.id ? "active" : ""} ${
                        disabled ? "disabled" : ""
                      }`}
                      onClick={() => !disabled && setPickTweak(t.id)}
                      disabled={disabled}
                      title={
                        disabled
                          ? "Train your clone first to mirror its natural length."
                          : t.description
                      }
                    >
                      <i className={`fa-solid ${t.icon}`} />
                      <span className="vct-tweak-label">{t.label}</span>
                      <span className="vct-tweak-meta">
                        {disabled
                          ? "needs trained clone"
                          : `~${previewW} words`}
                      </span>
                    </button>
                  );
                })}
              </div>

              {pickTweak === "custom" && (
                <CustomWordsField
                  value={pickCustomWords}
                  onCommit={(words) => setPickCustomWords(words)}
                />
              )}

              {/* Live preview blueprint that updates as the tweak changes */}
              <div className="vct-tweak-preview">
                <div className="vct-tweak-preview-head">
                  <i className="fa-solid fa-chart-column" />
                  Preview — <strong>{pickedStructure?.label}</strong> ·{" "}
                  {LENGTH_TWEAK_BY_ID[pickTweak]?.label}
                  <span className="vct-tweak-preview-words">
                    ~{previewWords} words
                  </span>
                </div>
                <StructureBlueprint
                  structureId={pickStructure}
                  tweakId={pickTweak}
                  cloneAvgWords={cloneAvgWords}
                  customWords={
                    pickTweak === "custom" ? pickCustomWords : undefined
                  }
                  showWords
                  size="lg"
                />
                {/* Same per-variant conflicts as the cards — but for the
                    in-flight selection so the user sees the warning BEFORE
                    they commit. Synthesizes a minimal variant-shape from
                    the current picker state. */}
                <ConflictChips
                  conflicts={computeVariantConflicts(
                    clone,
                    {
                      structureId: pickStructure,
                      lengthTweakId: pickTweak,
                      customWords: pickTweak === "custom" ? pickCustomWords : undefined,
                    },
                    cloneAvgWords,
                  )}
                  compact
                />
              </div>

              <div className="vct-picker-actions">
                <button
                  type="button"
                  className="vct-btn-ghost"
                  onClick={resetPicker}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="vct-btn-primary"
                  onClick={handleAdd}
                >
                  <i className="fa-solid fa-plus" />
                  Add variant
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="vct-add-variant"
          onClick={() => setPickerOpen(true)}
        >
          <i className="fa-solid fa-plus" />
          Add structure variant
        </button>
      )}
    </div>
  );
};

// --------------------- Subject lines list ---------------------

const SubjectLinesList = ({ subjectLines, onAdd, onUpdate, onRemove, smartEnabled, onToggleSmart }) => (
  <div className="vct-subjects">
    <div className="vct-subjects-toggle">
      <button
        type="button"
        className={`vct-toggle-row ${smartEnabled ? "on" : "off"}`}
        onClick={() => onToggleSmart(!smartEnabled)}
      >
        <i className={`fa-solid ${smartEnabled ? "fa-toggle-on" : "fa-toggle-off"}`} />
        <div className="vct-toggle-row-body">
          <div className="vct-toggle-row-title">Smart subject (AI-generated)</div>
          <div className="vct-toggle-row-hint">
            Voice DNA writes the subject for each contact based on the chosen variant.
          </div>
        </div>
      </button>
    </div>

    {!smartEnabled && (
      <>
        <div className="vct-subjects-list">
          {subjectLines.map((s, i) => (
            <div key={s.id} className="vct-subject-row">
              <input
                type="text"
                className="vct-input"
                value={s.text}
                onChange={(e) => onUpdate(i, e.target.value)}
                placeholder="e.g. quick question about {{company}}"
              />
              {subjectLines.length > 1 && (
                <button
                  type="button"
                  className="vct-subject-remove"
                  onClick={() => onRemove(i)}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" className="vct-btn-ghost-sm" onClick={onAdd}>
          <i className="fa-solid fa-plus" /> Add subject variant
        </button>
      </>
    )}
  </div>
);

// =============================================================================
// FIELD SELECTOR — picks which contact fields the AI is allowed to use for
// this campaign. Renders TWO sub-grids:
//   1. Standard fields (curated set of `BASE_AVAILABLE_FIELDS` from the World
//      table — same keys the rest of the pipeline already passes around).
//   2. Custom fields (per-user, defined in Admin → Custom Fields, persisted at
//      `World/{userId}/settings/customFields`). The default selection is seeded
//      from the admin-side `useAsEmailContext` flag, but here it can be
//      overridden per-campaign.
//
// All selections live on `cloneConfig.fieldConfig`:
//   { enabledStandardKeys: string[], enabledCustomIds: string[] }
// =============================================================================

const FieldChip = ({ enabled, onToggle, icon, label, badge, locked, hint }) => (
  <button
    type="button"
    className={`vct-field-chip ${enabled ? "on" : "off"} ${locked ? "locked" : ""}`}
    onClick={locked ? undefined : onToggle}
    title={hint || (enabled ? "Click to exclude from AI" : "Click to include")}
  >
    <span className="vct-field-chip-icon">
      <i className={`fa-solid ${icon}`} />
    </span>
    <span className="vct-field-chip-body">
      <span className="vct-field-chip-label">{label}</span>
      {badge && <span className="vct-field-chip-badge">{badge}</span>}
    </span>
    <span className="vct-field-chip-toggle">
      <i className={`fa-solid ${enabled ? "fa-circle-check" : "fa-circle"}`} />
    </span>
  </button>
);

const FieldSelector = ({
  enabledStandardKeys,
  enabledCustomIds,
  customFields,
  customFieldsLoading,
  onToggleStandard,
  onToggleCustom,
  onSelectAllStandard,
  onResetStandard,
}) => {
  const standardOnCount = STANDARD_PERSONALIZATION_FIELDS.filter((f) =>
    enabledStandardKeys.includes(f.key),
  ).length;
  const customOnCount = (customFields || []).filter((f) =>
    (enabledCustomIds || []).includes(f.id),
  ).length;

  return (
    <div className="vct-fields">
      {/* Standard fields */}
      <div className="vct-fields-group">
        <div className="vct-fields-group-head">
          <div className="vct-fields-group-title">
            <i className="fa-solid fa-table-list" />
            Standard table fields
            <span className="vct-fields-group-count">
              {standardOnCount}/{STANDARD_PERSONALIZATION_FIELDS.length}
            </span>
          </div>
          <div className="vct-fields-group-actions">
            <button
              type="button"
              className="vct-fields-mini-btn"
              onClick={onSelectAllStandard}
              title="Enable every standard field"
            >
              All on
            </button>
            <button
              type="button"
              className="vct-fields-mini-btn"
              onClick={onResetStandard}
              title="Reset to recommended baseline"
            >
              Reset
            </button>
          </div>
        </div>
        <div className="vct-field-grid">
          {STANDARD_PERSONALIZATION_FIELDS.map((f) => (
            <FieldChip
              key={f.key}
              enabled={enabledStandardKeys.includes(f.key)}
              onToggle={() => onToggleStandard(f.key)}
              icon={f.icon}
              label={f.label}
              badge={f.essential ? "Essential" : null}
              hint={
                f.essential
                  ? "Recommended for any personalization. You can still turn it off."
                  : undefined
              }
            />
          ))}
        </div>
      </div>

      {/* Custom fields */}
      <div className="vct-fields-group">
        <div className="vct-fields-group-head">
          <div className="vct-fields-group-title">
            <i className="fa-solid fa-sliders" />
            Custom fields
            <span className="vct-fields-group-count">
              {customOnCount}/{(customFields || []).length}
            </span>
          </div>
          <button
            type="button"
            className="vct-fields-group-link"
            onClick={() => {
              // Cross-component tab switch — handler in WorldSettings.js.
              document.dispatchEvent(
                new CustomEvent("openSettingsTab", {
                  detail: { tab: "customFields" },
                }),
              );
            }}
            title="Define more custom fields under Admin → Custom Fields"
          >
            <i className="fa-solid fa-gear" />
            Manage in Admin
          </button>
        </div>
        {customFieldsLoading ? (
          <div className="vct-fields-loading">
            <div className="vct-spinner" />
            Loading custom fields…
          </div>
        ) : (customFields || []).length === 0 ? (
          <div className="vct-fields-empty">
            <i className="fa-solid fa-circle-info" />
            No custom fields yet. Add some under{" "}
            <strong>Admin → Custom Fields</strong> to give the AI extra
            campaign-specific context (e.g. industry, signal, persona).
          </div>
        ) : (
          <div className="vct-field-grid">
            {(customFields || []).map((f) => (
              <FieldChip
                key={f.id}
                enabled={(enabledCustomIds || []).includes(f.id)}
                onToggle={() => onToggleCustom(f.id)}
                icon={
                  f.fieldType === "number"
                    ? "fa-hashtag"
                    : "fa-tag"
                }
                label={f.label || f.key}
                badge={
                  f.useAsEmailContext ? "Admin default" : null
                }
                hint={
                  f.useAsEmailContext
                    ? "Marked as AI context in Admin. On by default for new campaigns."
                    : "Hidden from AI in Admin — opt in here only if you want this campaign to use it."
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// ENHANCEMENTS PANEL — small toggle row for additive capabilities (live web
// research, prior interaction history, sender context). Separate from the
// field selector because these aren't field selections.
// =============================================================================

const EnhancementsPanel = ({ enhancements, onToggle }) => {
  const t = useT();
  return (
    <div className="vct-enhancements">
      {INFO_ENHANCEMENTS.map((s) => {
        const on = enhancements?.[s.id];
        const label = s.labelKey ? t(s.labelKey) : s.label;
        const description = s.descKey ? t(s.descKey) : s.description;
        return (
          <button
            key={s.id}
            type="button"
            className={`vct-enhancement ${on ? "on" : "off"}`}
            onClick={() => onToggle(s.id, !on)}
          >
            <span className="vct-enhancement-icon">
              <i className={`fa-solid ${s.icon}`} />
            </span>
            <span className="vct-enhancement-body">
              <span className="vct-enhancement-title">{label}</span>
              <span className="vct-enhancement-desc">{description}</span>
            </span>
            <span className="vct-enhancement-toggle">
              <i className={`fa-solid ${on ? "fa-toggle-on" : "fa-toggle-off"}`} />
            </span>
          </button>
        );
      })}
    </div>
  );
};

// =============================================================================
// SIGN-OFF IDENTITY PANEL — per-template override for "who signs this email".
//
// Fixes the "Viele Grüße / Sender" artefact: when workspace `user_name` is
// empty (common with team workspaces that have multiple sender accounts but
// no global identity), the model used to default to literal "Sender" in the
// sign-off because it had nothing else to anchor to. The Custom mode here
// lets the template explicitly pin the identity used in the prompt's sender
// block (and downstream in resolveSignOff's placeholder substitution).
// =============================================================================
const SignOffIdentityPanel = ({ value, onChange }) => {
  const t = useT();
  const v = resolveSignOffIdentity(value);
  const isCustom = v.mode === SIGN_OFF_IDENTITY_MODES.CUSTOM;

  const updateMode = (mode) => onChange({ ...v, mode });
  const updateField = (key, next) => onChange({ ...v, mode: v.mode, [key]: next });

  return (
    <div className="vct-soi">
      <div className="vct-soi-modes">
        <button
          type="button"
          className={`vct-soi-mode ${!isCustom ? "active" : ""}`}
          onClick={() => updateMode(SIGN_OFF_IDENTITY_MODES.SENDER)}
        >
          <i className="fa-solid fa-paper-plane" />
          <span className="vct-soi-mode-title">{t("signOffIdentityAuto")}</span>
          <span className="vct-soi-mode-desc">{t("signOffIdentityAutoDesc")}</span>
        </button>
        <button
          type="button"
          className={`vct-soi-mode ${isCustom ? "active" : ""}`}
          onClick={() => updateMode(SIGN_OFF_IDENTITY_MODES.CUSTOM)}
        >
          <i className="fa-solid fa-id-badge" />
          <span className="vct-soi-mode-title">{t("signOffIdentityCustom")}</span>
          <span className="vct-soi-mode-desc">{t("signOffIdentityCustomDesc")}</span>
        </button>
      </div>

      {isCustom && (
        <div className="vct-soi-fields">
          <label className="vct-soi-field">
            <span className="vct-soi-field-label">
              {t("signOffIdentityNameLabel")}
            </span>
            <input
              type="text"
              className="vct-soi-input"
              value={v.name}
              placeholder={t("signOffIdentityNamePlaceholder")}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => updateField("name", e.target.value)}
            />
            <span className="vct-soi-field-hint">
              {t("signOffIdentityNameHint")}
            </span>
          </label>
          <label className="vct-soi-field">
            <span className="vct-soi-field-label">
              {t("signOffIdentityRoleLabel")}
            </span>
            <input
              type="text"
              className="vct-soi-input"
              value={v.role}
              placeholder={t("signOffIdentityRolePlaceholder")}
              autoComplete="off"
              onChange={(e) => updateField("role", e.target.value)}
            />
          </label>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// GREETING RULES PANEL — per-template control over the email's opening line.
//
// Two cases the user told us about:
//   1. Decision maker known (we have a first name)  → "Hey Dieter," / "Sehr geehrter Herr Rotlauer,"
//   2. Only info@ / generic address (no DM first name) → "Liebes Acme-Team,"
//
// When mode === 'auto', we don't add any directive and the voice DNA's
// natural opener style continues to drive the greeting — that's the
// pre-feature behavior so old templates render unchanged.
// =============================================================================
// Sample contexts intentionally use a business name with a legal-form
// suffix so the user can see how {{businessNameShort}} resolves
// ("Müller Optik GmbH" → "Müller Optik") right in the preview.
const SAMPLE_GREETING_CTX_WITH = {
  firstName:     "Dieter",
  lastName:      "Rotlauer",
  recipientName: "Dieter Rotlauer",
  businessName:  "Müller Optik GmbH",
};
const SAMPLE_GREETING_CTX_WITHOUT = {
  firstName:     "",
  lastName:      "",
  recipientName: "",
  businessName:  "Müller Optik GmbH",
};

// Order matters here — left → right reads as "less personalization"
// → "more personalization". Used by the slider UI.
const GREETING_COMPANY_MENTION_OPTIONS = [
  { id: GREETING_COMPANY_MENTION.AUTO,    icon: "fa-wand-magic-sparkles", labelKey: "greetingCompanyMentionAuto" },
  { id: GREETING_COMPANY_MENTION.INCLUDE, icon: "fa-circle-check",        labelKey: "greetingCompanyMentionInclude" },
  { id: GREETING_COMPANY_MENTION.EXCLUDE, icon: "fa-ban",                 labelKey: "greetingCompanyMentionExclude" },
];

const GreetingRulesPanel = ({ value, onChange }) => {
  const t = useT();
  const v = resolveGreeting(value);
  const isConfigured = v.mode === GREETING_MODES.CONFIGURED;

  const updateMode = (mode) => onChange({ ...v, mode });
  const updateField = (key, next) => onChange({ ...v, [key]: next });

  const previewWith = applyGreetingPlaceholders(
    v.withFirstName || GREETING_DEFAULT_WITH_FIRST_NAME,
    SAMPLE_GREETING_CTX_WITH,
  );
  const previewWithout = applyGreetingPlaceholders(
    v.withoutFirstName || GREETING_DEFAULT_WITHOUT_FIRST_NAME,
    SAMPLE_GREETING_CTX_WITHOUT,
  );
  // Heuristic preview: shows the user what the short form will look like
  // for the sample business name so they can see "Müller Optik GmbH" →
  // "Müller Optik" without saving + opening a real send.
  const shortPreview = buildShortBusinessName(SAMPLE_GREETING_CTX_WITHOUT.businessName);

  const contextLevelIdx = Math.max(
    0,
    GREETING_COMPANY_CONTEXT_LEVELS.findIndex(
      (l) => l.id === v.companyContextLevel,
    ),
  );

  return (
    <div className="vct-greet">
      <div className="vct-greet-modes">
        <button
          type="button"
          className={`vct-greet-mode ${!isConfigured ? "active" : ""}`}
          onClick={() => updateMode(GREETING_MODES.AUTO)}
        >
          <i className="fa-solid fa-wand-magic-sparkles" />
          <span className="vct-greet-mode-title">{t("greetingModeAuto")}</span>
          <span className="vct-greet-mode-desc">{t("greetingModeAutoDesc")}</span>
        </button>
        <button
          type="button"
          className={`vct-greet-mode ${isConfigured ? "active" : ""}`}
          onClick={() => updateMode(GREETING_MODES.CONFIGURED)}
        >
          <i className="fa-solid fa-pen-ruler" />
          <span className="vct-greet-mode-title">
            {t("greetingModeConfigured")}
          </span>
          <span className="vct-greet-mode-desc">
            {t("greetingModeConfiguredDesc")}
          </span>
        </button>
      </div>

      {isConfigured && (
        <>
          <div className="vct-greet-fields">
            <label className="vct-greet-field">
              <span className="vct-greet-field-label">
                <i className="fa-solid fa-user-tag" />
                {t("greetingWithFirstNameLabel")}
              </span>
              <input
                type="text"
                className="vct-greet-input"
                value={v.withFirstName}
                placeholder={t("greetingWithFirstNamePlaceholder")}
                onChange={(e) => updateField("withFirstName", e.target.value)}
              />
              <span className="vct-greet-hint">
                {t("greetingWithFirstNameHint")}
              </span>
            </label>
            <label className="vct-greet-field">
              <span className="vct-greet-field-label">
                <i className="fa-solid fa-building" />
                {t("greetingWithoutFirstNameLabel")}
              </span>
              <input
                type="text"
                className="vct-greet-input"
                value={v.withoutFirstName}
                placeholder={t("greetingWithoutFirstNamePlaceholder")}
                onChange={(e) => updateField("withoutFirstName", e.target.value)}
              />
              <span className="vct-greet-hint">
                {t("greetingWithoutFirstNameHint")}
              </span>
            </label>
          </div>

          <div className="vct-greet-preview">
            <div className="vct-greet-preview-head">
              <i className="fa-solid fa-eye" />
              {t("greetingPreviewHeader")}
            </div>
            <div className="vct-greet-preview-row">
              <span className="vct-greet-preview-label">
                {t("greetingPreviewWithName")}
              </span>
              <span className="vct-greet-preview-value">{previewWith}</span>
            </div>
            <div className="vct-greet-preview-row">
              <span className="vct-greet-preview-label">
                {t("greetingPreviewWithoutName")}
              </span>
              <span className="vct-greet-preview-value">{previewWithout}</span>
            </div>
            <div className="vct-greet-preview-row vct-greet-preview-shortform">
              <span className="vct-greet-preview-label">
                {"{{businessNameShort}}"}
              </span>
              <span className="vct-greet-preview-value">
                {SAMPLE_GREETING_CTX_WITHOUT.businessName} → <strong>{shortPreview}</strong>
              </span>
            </div>
          </div>
        </>
      )}

      {/* Personalization controls — visible in BOTH modes so the user can
          shape how the AI tightens / loosens the greeting's company tie-in
          regardless of whether the literal opener is locked. */}
      <div className="vct-greet-personalization">
        <div className="vct-greet-personalization-head">
          <i className="fa-solid fa-sliders" />
          <div className="vct-greet-personalization-titles">
            <span className="vct-greet-personalization-title">
              {t("greetingPersonalizationTitle")}
            </span>
            <span className="vct-greet-personalization-hint">
              {t("greetingPersonalizationHint")}
            </span>
          </div>
        </div>

        <div className="vct-greet-toggle">
          <span className="vct-greet-toggle-label">
            <i className="fa-solid fa-building-user" />
            {t("greetingCompanyMentionLabel")}
          </span>
          <div className="vct-greet-toggle-row" role="radiogroup">
            {GREETING_COMPANY_MENTION_OPTIONS.map((opt) => {
              const active = v.companyMention === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`vct-greet-toggle-btn ${active ? "active" : ""}`}
                  onClick={() => updateField("companyMention", opt.id)}
                >
                  <i className={`fa-solid ${opt.icon}`} />
                  <span>{t(opt.labelKey)}</span>
                </button>
              );
            })}
          </div>
          <span className="vct-greet-hint">
            {t("greetingCompanyMentionHint")}
          </span>
        </div>

        <div className="vct-greet-slider">
          <div className="vct-greet-slider-head">
            <span className="vct-greet-toggle-label">
              <i className="fa-solid fa-gauge-high" />
              {t("greetingContextLevelLabel")}
            </span>
            <span className="vct-greet-slider-active">
              {t(`greetingContext_${v.companyContextLevel}_label`)}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max={GREETING_COMPANY_CONTEXT_LEVELS.length - 1}
            step="1"
            value={contextLevelIdx}
            className="vct-greet-slider-input"
            onChange={(e) =>
              updateField(
                "companyContextLevel",
                GREETING_COMPANY_CONTEXT_LEVELS[Number(e.target.value)].id,
              )
            }
            aria-label={t("greetingContextLevelLabel")}
          />
          <div className="vct-greet-slider-ticks">
            {GREETING_COMPANY_CONTEXT_LEVELS.map((lvl, idx) => {
              const active = idx === contextLevelIdx;
              return (
                <button
                  key={lvl.id}
                  type="button"
                  className={`vct-greet-slider-tick ${active ? "active" : ""}`}
                  onClick={() => updateField("companyContextLevel", lvl.id)}
                  title={t(`greetingContext_${lvl.id}_desc`)}
                >
                  <span className="vct-greet-slider-tick-label">
                    {t(`greetingContext_${lvl.id}_label`)}
                  </span>
                </button>
              );
            })}
          </div>
          <span className="vct-greet-hint">
            {t(`greetingContext_${v.companyContextLevel}_desc`)}
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// LIVE PREVIEW PANEL — sticky right-side preview that mirrors the live
// generation pipeline using a small/fast model (Haiku) so the user can iterate
// on the campaign config and instantly see how an email would come out.
//
// State machine:
//   1. BLOCKED   — basics missing → requirements banner + disabled Generate
//   2. READY     — basics complete → ready banner + active Generate button
//   3. GENERATING — backend (Haiku) is running (Step 6 wires this)
//   4. GENERATED — preview email is shown with regenerate / switch-variant
//                  controls (Step 6 wires this)
//
// The generation backend call (`previewVoiceCloneEmail` Cloud Function)
// arrives in Step 6 — for now Generate is a stub that surfaces a "coming
// soon" message so the wiring is testable today.
// =============================================================================

// Compute what's missing right now and surface it as a checklist + a
// human-readable count for the banner header.
const computePreviewRequirements = ({
  clone,
  cloneConfig,
  templateName,
}) => {
  const checks = [
    {
      id: "name",
      label: "Name your campaign",
      done: !!(templateName && templateName.trim()),
      hint: "Set a recognizable name in the header.",
    },
    {
      id: "clone",
      label: "Pick a CopyTwin",
      done: !!clone,
      hint: "Choose one in the CopyTwin section.",
    },
    {
      id: "trained",
      label: "CopyTwin is trained",
      done: !!clone?.voiceDNA,
      hint: clone
        ? "Open the twin in Email → CopyTwin AI and add a few sample emails."
        : "Pick a CopyTwin first, then make sure it has Voice DNA.",
    },
    {
      id: "variant",
      label: "Add at least one active variant",
      // In CopyTwin structure mode the structure comes from the twin itself —
      // no active variant is required, so this requirement is auto-satisfied.
      done:
        resolveStructureSource(cloneConfig) === STRUCTURE_SOURCES.TWIN
          ? true
          : (cloneConfig.variants || []).some((v) => v.active),
      hint: "Pick a structure in the Structure variants section below.",
    },
  ];

  const missing = checks.filter((c) => !c.done);
  return { checks, missing, ready: missing.length === 0 };
};

// QualitySelector — neutral 4-step quality knob. Internally maps to engine +
// max tokens + thinking budget (see QUALITY_TIERS). The user never sees model
// names — to them it's all "BonusClub AI" with a quality scale.
const QualitySelector = ({ value, onChange }) => {
  const active = resolveQualityTier(value);
  return (
    <div className="vct-qual">
      <div className="vct-qual-head">
        <i className="fa-solid fa-microchip" />
        <span className="vct-qual-head-label">AI quality</span>
      </div>
      <div className="vct-qual-row" role="radiogroup" aria-label="AI quality tier">
        {QUALITY_TIERS.map((t) => {
          const selected = t.id === active.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`vct-qual-btn ${selected ? "active" : ""}`}
              onClick={() => onChange(t.id)}
              data-tier={t.id}
              title={`${t.tagline} (${formatCreditCost(t.creditCost)} credits)`}
            >
              <i className={`fa-solid ${t.icon}`} />
              <span className="vct-qual-btn-label">{t.label}</span>
              <span
                className="vct-qual-btn-bars"
                aria-label={`Quality ${t.quality} of 4`}
              >
                {[1, 2, 3, 4].map((n) => (
                  <span
                    key={n}
                    className={`vct-qual-bar ${
                      n <= t.quality ? "on" : "off"
                    }`}
                  />
                ))}
              </span>
              <span className="vct-qual-btn-cost">
                {formatCreditCost(t.creditCost)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// useTestRecipients — pulls a small pool of REAL leads from the user's World
// DB so the live preview can generate against actual recipient data instead
// of a hard-coded sample. Defaults to "not-contacted" leads so the preview
// doesn't burn unique value on someone we already wrote to.
//
// Loads lazily (first time the picker is opened) and caches the result for
// the lifetime of the editor session — picking a different lead is free
// after that. If no leads exist (or the query fails), falls back to a
// sample contact so the editor never blocks on missing data.
// =============================================================================

const SAMPLE_TEST_CONTACT_ROW = {
  id: "__sample__",
  business_name:    "Müller Optik GmbH",
  lead_website:     "https://mueller-optik.example",
  lead_city:        "München",
  lead_country:     "Germany",
  lead_description: "Family-run optician with two stores in Munich. Specializes in independent designer frames and contact lens fittings.",
  lead_decision_makers: JSON.stringify([
    { name: "Sarah Müller", role: "Inhaberin", email: "sarah@mueller-optik.example" },
  ]),
  lead_email:       "sarah@mueller-optik.example",
  lead_telephone:   "",
  lead_socials:     "",
  lead_instagram_followers: "",
  lead_rating:      "",
  lead_reviews:     "",
  __isSample:       true,
};

// Convert a Firestore lead row into the contact payload the backend prompt
// builder expects. Reuses buildContactBlock so the preview is byte-identical
// to a real send for the same lead — no parity drift.
const rowToContactPayload = (row) => {
  if (!row) return null;
  const recipient = getRecipientInfoForContact(row, "");
  const firstName = getFirstDmName(row);
  return buildContactBlock(row, recipient, { firstName, customFieldsData: [] });
};

// =============================================================================
// useWorkspaceSender — subscribes to /World/{userId}/settings/email so the
// preview can use the REAL sender identity (name, role, email) instead of
// firing the prompt with empty strings. Without this, the model:
//   - sees an empty senderName and either invents one or signs the email "Best,"
//   - replaces {{firstName}} placeholders in fixed sign-offs with nothing
// Returned shape mirrors `buildSenderBlock(userSettings)` exactly so the
// payload can use it as-is. When the workspace has no user_name configured
// yet, falls back to a clearly-labeled "Sample Sender" so the preview still
// renders cleanly instead of breaking the sign-off.
// =============================================================================
const SAMPLE_SENDER_FALLBACK = {
  senderName:  "Sample Sender",
  firstName:   "Sample",
  senderEmail: "",
  senderRole:  "Marketing",
};

const useWorkspaceSender = (userId) => {
  const [sender, setSender] = useState(SAMPLE_SENDER_FALLBACK);

  useEffect(() => {
    if (!userId) return undefined;
    const settingsRef = ref(database, `/World/${userId}/settings/email`);
    const unsub = onValue(settingsRef, (snapshot) => {
      if (!snapshot.exists()) {
        setSender(SAMPLE_SENDER_FALLBACK);
        return;
      }
      const built = buildSenderBlock(snapshot.val() || {});
      // If the workspace has no sender name set yet, keep the labeled
      // sample so the preview never produces a nameless sign-off.
      if (!built.senderName) {
        setSender(SAMPLE_SENDER_FALLBACK);
        return;
      }
      setSender(built);
    });
    return () => unsub();
  }, [userId]);

  return sender;
};

const useTestRecipients = (userId) => {
  const [recipients, setRecipients] = useState([]); // raw Firestore docs
  const [loading,    setLoading]    = useState(false);
  const [loaded,     setLoaded]     = useState(false);
  const [error,      setError]      = useState("");

  const load = useCallback(async () => {
    if (!userId || loaded || loading) return;
    setLoading(true);
    setError("");
    try {
      // Pull up to 25 not-contacted leads with at least a business name.
      // We cap small because this fuels a session-scoped picker, not a
      // searchable list. The user can roll/cycle within these 25.
      const dbRef = collection(firestore, `World/${userId}/db`);
      const q = query(
        dbRef,
        where("lead_contacted", "in", [null, "", "Not Contacted"]),
        limit(25),
      );
      const snap = await getDocs(q);
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.business_name && !r.trashedAt);
      setRecipients(rows);
    } catch (err) {
      console.warn("[VoiceClone Preview] Failed to load test recipients", err);
      setError(err?.message || "Couldn't load leads");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [userId, loaded, loading]);

  return { recipients, loading, loaded, error, load };
};

// =============================================================================
// TestRecipientPicker — compact UI in the preview panel: shows the current
// test recipient (avatar + name + business), with a "Roll random" button
// to cycle and a dropdown to pick explicitly. Hides everything behind a
// neutral pill until the user opens it, so the preview chrome stays calm.
// =============================================================================
const TestRecipientPicker = ({
  selected,        // { id, business_name, ... } | null → use sample
  onChange,        // (rowOrNull) => void
  pool,            // Firestore docs from useTestRecipients
  loading,
  loaded,
  onLoad,          // call to lazy-load the pool
}) => {
  const t = useT();
  const [open, setOpen] = useState(false);

  const effective = selected || SAMPLE_TEST_CONTACT_ROW;
  const isSample = !!effective.__isSample;

  // Lazy-load on first open.
  useEffect(() => {
    if (open && !loaded && !loading) onLoad();
  }, [open, loaded, loading, onLoad]);

  const rollRandom = () => {
    if (!pool || pool.length === 0) {
      onChange(null); // fall back to sample
      return;
    }
    // Pick a different one if possible — avoids the "click roll, same lead"
    // surprise when the user has multiple leads loaded.
    let pick;
    if (pool.length === 1) {
      pick = pool[0];
    } else {
      const others = pool.filter((p) => p.id !== effective.id);
      pick = others[Math.floor(Math.random() * others.length)];
    }
    onChange(pick);
  };

  const recipientLabel = (() => {
    const dm = getFirstDmName(effective) || "";
    const biz = effective.business_name || "";
    if (dm && biz) return `${dm} · ${biz}`;
    return dm || biz || t("testRecipientFallback");
  })();

  return (
    <div className="vct-testrecipient">
      <div className="vct-testrecipient-row">
        <div className="vct-testrecipient-avatar" aria-hidden="true">
          <i className="fa-solid fa-user" />
        </div>
        <div className="vct-testrecipient-meta">
          <div className="vct-testrecipient-label">{t("testRecipientLabel")}</div>
          <div className="vct-testrecipient-name" title={recipientLabel}>
            {recipientLabel}
            {isSample && (
              <span className="vct-testrecipient-sampletag">SAMPLE</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="vct-testrecipient-btn"
          onClick={() => {
            if (!loaded && !loading) onLoad();
            rollRandom();
          }}
          title={t("testRecipientRoll")}
        >
          <i className="fa-solid fa-dice" />
          {t("testRecipientRoll")}
        </button>
        <button
          type="button"
          className={`vct-testrecipient-btn ${open ? "active" : ""}`}
          onClick={() => setOpen((v) => !v)}
          title={t("testRecipientChange")}
          aria-expanded={open}
        >
          <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`} />
        </button>
      </div>

      {open && (
        <div className="vct-testrecipient-dropdown">
          {loading && (
            <div className="vct-testrecipient-state">
              <i className="fa-solid fa-spinner fa-spin" />
              {t("testRecipientLoading")}
            </div>
          )}
          {!loading && loaded && pool.length === 0 && (
            <div className="vct-testrecipient-state">
              <i className="fa-solid fa-circle-info" />
              {t("testRecipientNone")}
            </div>
          )}
          {!loading && pool.length > 0 && (
            <ul className="vct-testrecipient-list">
              <li>
                <button
                  type="button"
                  className={`vct-testrecipient-item ${
                    isSample ? "active" : ""
                  }`}
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <i className="fa-solid fa-flask" />
                  <span>{t("testRecipientFallback")}</span>
                </button>
              </li>
              {pool.map((row) => {
                const dm = getFirstDmName(row) || "";
                const biz = row.business_name || "(no business name)";
                const isActive = row.id === effective.id;
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      className={`vct-testrecipient-item ${
                        isActive ? "active" : ""
                      }`}
                      onClick={() => {
                        onChange(row);
                        setOpen(false);
                      }}
                    >
                      <i className="fa-solid fa-user" />
                      <span>
                        <strong>{dm || "—"}</strong>
                        <em>{biz}</em>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const LivePreviewPanel = ({
  userId,
  templateId,
  clone,
  cloneConfig,
  templateType,
  templateName,
  onQualityChange,
  ensurePersisted,
}) => {
  const { checks, missing, ready } = computePreviewRequirements({
    clone,
    cloneConfig,
    templateName,
  });
  const qualityTier = resolveQualityTier(cloneConfig.qualityTier);
  const { balance: creditBalance } = useCreditBalance(userId);

  // True when the user can't afford the current tier. Disables the button
  // even if requirements are met. We don't auto-downgrade — the user explicitly
  // chose this tier; we surface the gap so they can pick a cheaper one.
  const canAfford =
    creditBalance == null || creditBalance >= qualityTier.creditCost;
  const ableToGenerate = ready && canAfford;

  // Live preview state. previewResult holds the most recent generation:
  //   { subject, body, variantId, variantLabel, structureId, modelUsed }
  // The result panel renders body + subject when present and falls back to
  // the loading/error states otherwise.
  const [generating, setGenerating] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(0);
  const [previewResult, setPreviewResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // ─── Test recipient ────────────────────────────────────────────────────
  // The preview generates against a REAL lead from the user's World DB so
  // the output reflects what an actual send would produce (subject + body
  // both shaped by the recipient's research, decision makers, etc.).
  // Falls back to a sample contact when the workspace has no leads yet or
  // the user hasn't picked one explicitly.
  const [selectedTestRow, setSelectedTestRow] = useState(null); // null → use sample
  const testRecipients = useTestRecipients(userId);
  // Real workspace sender (user_name / user_email / user_role from settings).
  // Falls back to a labeled sample when the workspace has nothing configured
  // yet so the preview never produces a nameless sign-off.
  const previewSender = useWorkspaceSender(userId);

  // Build the contact payload at render time from the selected Firestore
  // row. Reusing buildContactBlock guarantees byte-identical mapping with
  // a real send — no lying preview. When the user hasn't picked a real
  // lead, we feed the sample row through the same converter for parity.
  const testContactPayload = useMemo(
    () => rowToContactPayload(selectedTestRow || SAMPLE_TEST_CONTACT_ROW),
    [selectedTestRow],
  );

  const handleGenerate = async () => {
    if (!ableToGenerate || generating) return;
    setGenerating(true);
    setErrorMessage("");

    // 0) Persist the latest draft to RTDB. The Voice-Clone editor uses a
    //    manual "Save" button, so on a brand-new template (just created via
    //    "+ Add new" → "Voice Clone") the template doesn't exist in the DB
    //    yet — backend would 500 with "Template not found". Same goes for
    //    any unsaved tweaks: we want Generate to use the live state, not the
    //    last-saved snapshot. This is a no-op when nothing changed.
    if (typeof ensurePersisted === "function") {
      try {
        await ensurePersisted();
      } catch (persistErr) {
        console.error("[VoiceClone Preview] persist before generate failed", persistErr);
        setErrorMessage(
          persistErr?.message
            ? `Couldn't save the template before generating: ${persistErr.message}`
            : "Couldn't save the template before generating. Please try again.",
        );
        setGenerating(false);
        return;
      }
    }

    // 1) Deduct credits FIRST. Fail fast if the user can't afford it.
    //    Mirrors the bulk/outreach pattern: charge before the AI call so
    //    the user can't burn cycles by tab-mashing.
    try {
      const deductCreditsFn = httpsCallable(functions, "deductCredits");
      await deductCreditsFn({
        action: CREDIT_ACTION_PREVIEW,
        amount: qualityTier.creditCost,
        worldId: userId,
        metadata: {
          templateId,
          cloneId: clone?.id,
          tier: qualityTier.id,
          engine: qualityTier.engine,
        },
      });
    } catch (err) {
      const msg = err?.message?.includes("resource-exhausted")
        ? `Not enough credits. You need ${formatCreditCost(
            qualityTier.creditCost,
          )} for a ${qualityTier.label} preview.`
        : "Credit deduction failed. Please try again.";
      setErrorMessage(msg);
      setGenerating(false);
      return;
    }

    // 2) Real generation — same dispatch the four AI modes use
    //    (mode: 'voiceClone'). The backend loads the clone + template from
    //    RTDB, picks a variant (weighted by reply rates) and writes in the
    //    trained voice. We don't ask for a fixed variantId — letting the
    //    backend pick keeps preview behavior identical to send behavior.
    try {
      const payload = {
        mode: "voiceClone",
        // Pass the active workspace userId so the backend can resolve the
        // template path correctly when the caller is operating inside a
        // switched team (resolveTeamUserId validates membership).
        userId,
        templateId,
        cloneId: clone?.id,
        // Real-lead contact (or sample fallback). Built via buildContactBlock
        // so the prompt input is byte-identical to a real send.
        contact: testContactPayload,
        // Real workspace sender — same shape buildVoiceClonePayload uses for
        // live sends, so the preview's sign-off resolution (fixed vs auto)
        // matches what an actual recipient would receive. Falls back to
        // SAMPLE_SENDER_FALLBACK ("Sample Sender" / "Marketing") when the
        // workspace has no user_name configured.
        sender: previewSender,
        // Mirror the same single-source-of-truth toggle the four AI modes
        // use (shouldGenerateSmartSubject). When the user has turned the
        // smart-subject toggle off, the preview shouldn't pretend the
        // backend produced one — that would misrepresent what real sends
        // look like.
        includeSubject: cloneConfig.smartSubject !== false,
      };

      const response = await authenticatedFetch(
        CLOUD_FUNCTION_URLS.worldEmailCreator(),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        let serverDetails = "";
        try {
          const errBody = await response.json();
          serverDetails = errBody.details || errBody.error || "";
        } catch (_) {}
        throw new Error(
          serverDetails
            ? `Preview generation failed: ${serverDetails}`
            : `Preview generation failed (${response.status}).`,
        );
      }

      const data = await response.json();
      setPreviewResult({
        body:          data.emailContent || "",
        subject:       data.subjectLine || "",
        variantId:     data.variantId || null,
        variantLabel:  data.variantLabel || "",
        structureId:   data.structureId || "",
        modelUsed:     data.modelUsed || "",
        targetWords:   data.targetWords || null,
      });
      setGeneratedAt(Date.now());
    } catch (err) {
      console.error("[VoiceClone Preview]", err);
      setErrorMessage(
        err.message || "Something went wrong while generating the preview.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const activeVariants = (cloneConfig.variants || []).filter((v) => v.active);
  // In CopyTwin structure mode the structure is mirrored from the twin, so the
  // "no active variant" blocker does not apply and the CTA meta reflects that.
  const isTwinStructure =
    resolveStructureSource(cloneConfig) === STRUCTURE_SOURCES.TWIN;

  return (
    <div className="vct-preview">
      <div className="vct-preview-head">
        <div className="vct-preview-eyebrow">
          <i className="fa-solid fa-eye" />
          Live preview
        </div>
        <div
          className="vct-preview-pill"
          title="Quick draft for iteration. Real sends use the full generation pipeline."
        >
          <i className="fa-solid fa-bolt" />
          Quick draft
        </div>
      </div>

      {/* Quality selector — sits above the requirements banner so the user
          decides "how good" before they generate. Persisted on cloneConfig. */}
      <QualitySelector
        value={qualityTier.id}
        onChange={onQualityChange}
      />

      {/* Requirements banner — only shown when blocked. The "ready" state
          is implicit (Generate button is enabled and shows clone+variant
          meta below it), so the redundant green banner is omitted to save
          vertical space in the preview panel. */}
      {!ready && (
        <div className="vct-preview-banner vct-preview-banner-blocked">
          <div className="vct-preview-banner-title">
            <i className="fa-solid fa-lock" />
            {missing.length === 1
              ? "1 thing missing"
              : `${missing.length} things missing`}
          </div>
          <ul className="vct-preview-checks">
            {checks.map((c) => (
              <li
                key={c.id}
                className={c.done ? "done" : "todo"}
                title={c.done ? "" : c.hint}
              >
                <i
                  className={`fa-solid ${
                    c.done ? "fa-circle-check" : "fa-circle"
                  }`}
                />
                <span className="vct-preview-check-label">{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Test recipient — pick a real lead from the World DB so the preview
          generation reflects what an actual send would produce (subject +
          body shaped by the recipient's research, decision makers, etc.).
          Falls back to a sample contact when the workspace has no leads. */}
      <TestRecipientPicker
        selected={selectedTestRow}
        onChange={setSelectedTestRow}
        pool={testRecipients.recipients}
        loading={testRecipients.loading}
        loaded={testRecipients.loaded}
        onLoad={testRecipients.load}
      />

      {/* Pre-send conflict nudge — mirror the ACTIONABLE (high/medium)
          clone × template conflicts right above Generate, so the user sees
          them at the exact moment before producing output (this is where
          "I accidentally set a template option that fights my CopyTwin"
          gets caught). Low-severity heads-ups stay only in the top-of-editor
          banner to keep this moment focused. Same detection source
          (computeTemplateConflicts) → no drift between the two surfaces. */}
      {clone?.voiceDNA && (() => {
        const preSendConflicts = computeTemplateConflicts(clone, cloneConfig)
          .filter((c) => c.severity === "high" || c.severity === "medium");
        if (preSendConflicts.length === 0) return null;
        return (
          <div className="vct-preview-conflicts">
            <div className="vct-preview-conflicts-head">
              <i className="fa-solid fa-circle-exclamation" />
              Before you generate
            </div>
            <ConflictChips conflicts={preSendConflicts} compact />
          </div>
        );
      })()}

      {/* NO ACTIVE VARIANT — hard, loud blocker. Without an active structure
          variant the backend rejects the send (NO_ACTIVE_VARIANT) and every
          real generation in Normal / Inbox / Bulk mode fails. We surface it
          right above Generate so the user can't miss it at the exact moment
          they'd otherwise hit a cryptic error. */}
      {clone?.voiceDNA && !isTwinStructure && activeVariants.length === 0 && (
        <div className="vct-novariant-alert" role="alert">
          <div className="vct-novariant-alert-head">
            <i className="fa-solid fa-triangle-exclamation" />
            Attention — no active variant
          </div>
          <div className="vct-novariant-alert-body">
            This template has no active structure variant, so emails
            <strong> cannot be generated</strong> (sends in Normal, Inbox and
            Bulk mode will fail too). Add and activate at least one variant in
            the <strong>Structure variants</strong> section to fix this.
          </div>
        </div>
      )}

      {/* Generate button — hidden during generation so the skeleton card
          below is the SOLE loading indicator (no duplicate "Generating…"
          state competing with "Crafting your preview"). Button + meta come
          back as "Regenerate" once the draft lands. */}
      {!generating && (
        <div className="vct-preview-cta">
          <button
            type="button"
            className={`vct-preview-generate ${
              ableToGenerate ? "ready" : "disabled"
            }`}
            onClick={handleGenerate}
            disabled={!ableToGenerate}
            title={
              !ready
                ? !isTwinStructure && activeVariants.length === 0
                  ? "No active variant — add and activate one in Structure variants first."
                  : "Complete the basics above first."
                : !canAfford
                  ? `Not enough credits. Need ${formatCreditCost(
                      qualityTier.creditCost,
                    )}, you have ${formatCreditCost(creditBalance || 0)}.`
                  : `Generate a preview at ${qualityTier.label} quality.`
            }
          >
            {generatedAt ? (
              <>
                <i className="fa-solid fa-rotate" />
                Regenerate · {formatCreditCost(qualityTier.creditCost)}
                <span className="vct-preview-generate-coin">
                  <i className="fa-solid fa-coins" />
                </span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-wand-magic-sparkles" />
                Generate · {formatCreditCost(qualityTier.creditCost)}
                <span className="vct-preview-generate-coin">
                  <i className="fa-solid fa-coins" />
                </span>
              </>
            )}
          </button>
          <div className="vct-preview-cta-meta">
            {!canAfford ? (
              <span className="vct-preview-cta-error">
                <i className="fa-solid fa-triangle-exclamation" />
                Need {formatCreditCost(qualityTier.creditCost)} credits, you
                have {formatCreditCost(creditBalance || 0)}
              </span>
            ) : (
              <>
                {ready && clone?.name && (
                  <span className="vct-preview-cta-clone">
                    <i className="fa-solid fa-wand-magic-sparkles" />
                    {clone.name} ·{" "}
                    {isTwinStructure
                      ? "CopyTwin structure"
                      : `${activeVariants.length} variant${
                          activeVariants.length === 1 ? "" : "s"
                        }`}
                  </span>
                )}
                {creditBalance != null && (
                  <span className="vct-preview-cta-balance">
                    {ready && clone?.name ? "·" : ""}{" "}
                    <strong>{formatCreditCost(creditBalance)}</strong> credits
                    available
                  </span>
                )}
              </>
            )}
          </div>
          {errorMessage && (
            <div className="vct-preview-cta-error-banner">
              <i className="fa-solid fa-circle-exclamation" />
              {errorMessage}
            </div>
          )}
        </div>
      )}

      {/* Result window — shown once Generate has been clicked at least once.
          Renders the actual generated email body + subject, plus a small
          meta strip (variant + structure) so the user can see which
          A/B-variant won the weighted-random pick this run. */}
      {(generating || generatedAt > 0) && (
        <div className="vct-preview-window">
          {generating ? (
            <div className="vct-preview-skeleton" aria-busy="true" aria-live="polite">
              <div className="vct-preview-skeleton-orb vct-preview-skeleton-orb-1" />
              <div className="vct-preview-skeleton-orb vct-preview-skeleton-orb-2" />
              <div className="vct-preview-skeleton-orb vct-preview-skeleton-orb-3" />
              <div className="vct-preview-skeleton-shimmer" />
              <div className="vct-preview-skeleton-logo">
                <img
                  src="/img/salesgod_dark.webp"
                  alt="BonusClub"
                  className="vct-preview-skeleton-logo-img"
                />
              </div>
              <div className="vct-preview-skeleton-text">
                <span className="vct-preview-skeleton-title">Crafting your preview</span>
                <span className="vct-preview-skeleton-subtitle">
                  BonusClub AI · {qualityTier.label}
                </span>
              </div>
            </div>
          ) : previewResult ? (
            <div className="vct-preview-email">
              {/* Inbox-style header — recipient + subject framed like a real
                  email so the user gets an honest sense of "what lands". The
                  variant tag is tucked top-right so it doesn't compete with
                  the subject for attention. */}
              <div className="vct-preview-email-headerframe">
                <div className="vct-preview-email-to">
                  <span className="vct-preview-email-to-label">To</span>
                  <span className="vct-preview-email-to-value">
                    {(() => {
                      const row = selectedTestRow || SAMPLE_TEST_CONTACT_ROW;
                      const dm = getFirstDmName(row) || "";
                      const biz = row.business_name || "";
                      return [dm, biz].filter(Boolean).join(" · ") || "—";
                    })()}
                  </span>
                  {previewResult.variantLabel && (
                    <span
                      className="vct-preview-email-variant-pill"
                      title="Active A/B variant for this draft"
                    >
                      <i className="fa-solid fa-shuffle" />
                      {previewResult.variantLabel}
                    </span>
                  )}
                </div>
                {previewResult.subject ? (
                  <div className="vct-preview-email-subject">
                    <span className="vct-preview-email-label">Subject</span>
                    <span className="vct-preview-email-subject-text">
                      {previewResult.subject}
                    </span>
                  </div>
                ) : (
                  <div className="vct-preview-email-subject vct-preview-email-subject-empty">
                    <span className="vct-preview-email-label">Subject</span>
                    <span className="vct-preview-email-subject-text">
                      <em>(no smart subject — using static line on send)</em>
                    </span>
                  </div>
                )}
                {previewResult.targetWords && (
                  <div className="vct-preview-email-meta">
                    <span className="vct-preview-email-words">
                      ~{previewResult.targetWords} words
                    </span>
                    {previewResult.modelUsed && (
                      <span className="vct-preview-email-model" title="Engine used for this draft">
                        <i className="fa-solid fa-microchip" />
                        {qualityTier.label}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="vct-preview-email-body">
                {previewResult.body}
              </div>
            </div>
          ) : (
            <div className="vct-preview-stub">
              <i className="fa-solid fa-clock" />
              No preview yet. Hit Generate to draft one.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// AUTO-SAVE INDICATOR — small passive tag that mirrors the auto-save state
// machine: idle/saved | saving | error | disabled (no clone yet).
//
// "dirty" is an INTERNAL state (drives the debounce timer) but is intentionally
// not surfaced visually — otherwise the tag would flicker between "saved" and
// "dirty" on every keystroke. Instead we keep showing the saved label while
// the user is typing.
//
// "saving" is debounced on display too: if the write completes within
// AUTO_SAVE_SHOW_SAVING_DELAY_MS we never flip to the spinner, so fast saves
// stay calm. Errors and the resting saved state reflect immediately.
// =============================================================================
const AUTO_SAVE_VARIANTS = {
  saved:    { icon: "fa-circle-check",         key: "autoSaveSaved"    },
  idle:     { icon: "fa-circle-check",         key: "autoSaveSaved"    },
  // dirty falls back to the saved variant — kept here so unexpected values
  // don't render a blank tag, but the editor never sets displayStatus="dirty".
  dirty:    { icon: "fa-circle-check",         key: "autoSaveSaved"    },
  saving:   { icon: "fa-circle-notch fa-spin", key: "autoSaveSaving"   },
  error:    { icon: "fa-triangle-exclamation", key: "autoSaveError"    },
  disabled: { icon: "fa-circle-pause",         key: "autoSaveDisabled" },
};

// How long the internal status must stay at "saving" before we actually
// flash the spinner. Below this threshold the user sees nothing change —
// the tag stays at "Gespeichert" throughout the (sub-second) write.
const AUTO_SAVE_SHOW_SAVING_DELAY_MS = 350;

const AutoSaveIndicator = ({ status, t }) => {
  const variant = AUTO_SAVE_VARIANTS[status] || AUTO_SAVE_VARIANTS.idle;
  return (
    <div
      className={`vct-autosave vct-autosave-${status}`}
      role="status"
      aria-live="polite"
    >
      <i className={`fa-solid ${variant.icon}`} />
      <span>{t(variant.key)}</span>
    </div>
  );
};

// =============================================================================
// MAIN EDITOR
// =============================================================================

const VoiceCloneTemplateEditor = ({
  userId,
  templateId,
  initialData,
  isSelected,
  onSelectionToggle,
  onClose,
  onDeleted,
}) => {
  const { clones, loading: clonesLoading } = useCopyClones(userId);
  const { customFields, contextFields, loading: customFieldsLoading } =
    useCustomFields(userId);
  const [draft, setDraft] = useState(initialData || {});
  const [savedAt, setSavedAt] = useState(0); // last successful auto-save timestamp
  const [message, setMessage] = useState(""); // transient error toasts (delete fail etc.)
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Auto-save state — replaces the manual Save button. The internal status
  // drives the write pipeline; the displayed status is a debounced projection
  // so the tag doesn't flicker between "saved" and "saving" on every
  // keystroke. Refs hold the debounce timer and the last-persisted signature
  // so we only POST when something actually changed (and we don't fire on the
  // initial hydration render).
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  const [displayStatus,  setDisplayStatus]  = useState("idle"); // what the header tag actually shows
  const autoSaveTimerRef       = useRef(null);
  const lastSavedSignatureRef  = useRef(null);
  const savedAtFadeTimerRef    = useRef(null);
  const displayDebounceRef     = useRef(null);

  // Sync incoming changes (e.g. live RTDB updates from another tab)
  useEffect(() => {
    if (!initialData) return;
    setDraft((prev) => ({ ...initialData, ...prev }));
    // Switching templates resets the auto-save baseline — the next render's
    // signature becomes the new "saved" snapshot. Without this, the new
    // template would look "dirty" immediately and trigger an unnecessary
    // write of the just-loaded data.
    lastSavedSignatureRef.current = null;
    setAutoSaveStatus("idle");
    // note: we don't overwrite local edits; stays merged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const cloneConfig = draft.cloneConfig || {};
  const variants = cloneConfig.variants || [];
  const subjectLines = draft.subjectLines || [];
  const variantStats = draft.stats?.variantStats || {};

  // Campaign language (string like "English" / "German" — same convention
  // as the standard pipeline) and the UI language derived from it. Only
  // English/German get a translated editor; everything else falls back to EN.
  const campaignLanguage = cloneConfig.language || CAMPAIGN_LANGUAGE_DEFAULT;
  const uiLang = resolveEditorUILanguage(campaignLanguage);

  // Tab state — left side is split into 3 progressive categories so the
  // most important controls are visible immediately and the rest is one
  // click away.
  const [activeTab, setActiveTab] = useState("essentials");

  // Local translator — same lookup as the `useT()` hook used by deeper
  // sub-components, but available inline without needing the provider.
  const tDict = EDITOR_TRANSLATIONS[uiLang] || EDITOR_TRANSLATIONS.en;
  const t = useCallback(
    (key) => tDict[key] ?? EDITOR_TRANSLATIONS.en[key] ?? key,
    [tDict],
  );

  // ---- fieldConfig (with safe defaults) ----
  // Standard keys default to the curated baseline if the template was saved
  // before fieldConfig existed.
  const enabledStandardKeys =
    cloneConfig.fieldConfig?.enabledStandardKeys ||
    DEFAULT_ENABLED_STANDARD_KEYS;
  // Custom IDs: `null` is the "not yet initialized" sentinel set by the
  // factory. We seed it from admin defaults (`useAsEmailContext: true`) the
  // first time we have custom fields loaded for this template.
  const enabledCustomIds = cloneConfig.fieldConfig?.enabledCustomIds;

  useEffect(() => {
    if (customFieldsLoading) return;
    // Seed using the latest draft (avoids closure-staleness across template
    // switches). Skip if already an array.
    setDraft((prev) => {
      const existing = prev.cloneConfig?.fieldConfig?.enabledCustomIds;
      if (Array.isArray(existing)) return prev;
      const adminDefaults = (contextFields || []).map((f) => f.id);
      return {
        ...prev,
        cloneConfig: {
          ...(prev.cloneConfig || {}),
          fieldConfig: {
            enabledStandardKeys:
              prev.cloneConfig?.fieldConfig?.enabledStandardKeys ||
              DEFAULT_ENABLED_STANDARD_KEYS,
            enabledCustomIds: adminDefaults,
          },
        },
      };
    });
    // Also re-evaluate when the active template changes — the new draft might
    // need its own seeding pass.
  }, [customFieldsLoading, contextFields, templateId]);

  // Backwards-compat: surface enhancements with the right defaults if the
  // template was created before this field existed.
  const enhancements =
    cloneConfig.enhancements ||
    INFO_ENHANCEMENTS.reduce((acc, s) => {
      acc[s.id] = s.defaultOn;
      return acc;
    }, {});

  const totalSent = draft.stats?.totalSent || 0;
  const totalReplies = draft.stats?.totalReplies || 0;
  const overallRate = replyRate(totalSent, totalReplies);

  const activeCount = variants.filter((v) => v.active).length;

  // Where the email STRUCTURE comes from: 'twin' = copy the CopyTwin's own
  // structure (variants optional), 'custom' = use the configured variants.
  const structureSource = resolveStructureSource(cloneConfig);
  const isTwinStructure = structureSource === STRUCTURE_SOURCES.TWIN;

  const patch = useCallback((p) => setDraft((prev) => ({ ...prev, ...p })), []);
  const patchClone = useCallback(
    (p) =>
      setDraft((prev) => ({
        ...prev,
        cloneConfig: { ...(prev.cloneConfig || {}), ...p },
      })),
    [],
  );

  const selectedClone = clones.find((c) => c.id === cloneConfig.cloneId);
  // Derive the clone's natural email length from training samples. Used by
  // variants whose tweak is "Mirror clone" so the AI matches the writer's
  // own rhythm instead of an arbitrary base.
  const cloneAvgWords = useMemo(
    () => computeCloneAvgEmailWords(selectedClone),
    [selectedClone],
  );

  // ---------- Tab progress (must-haves per tab) ----------
  // We only count the inputs the AI absolutely needs to produce a maximally
  // relevant email — not every optional knob. Keeps the indicator honest
  // and motivating.
  const tabProgress = useMemo(() => {
    const briefSpecial = (cloneConfig.uniqueAdvantage || "").trim().length > 0;
    const briefPain    = (cloneConfig.painPoint       || "").trim().length > 0;
    const briefGoal    = (cloneConfig.goal            || "").trim().length > 0;
    const hasClone     = !!cloneConfig.cloneId;
    const hasVariant   = (cloneConfig.variants || []).some((v) => v.active);

    const essentialsItems = [hasClone, briefSpecial, briefPain, briefGoal, hasVariant];
    const essentialsFilled = essentialsItems.filter(Boolean).length;

    const personalizationItems = [
      (enabledStandardKeys || []).length > 0,
    ];
    const personalizationFilled = personalizationItems.filter(Boolean).length;

    return {
      essentials:      { filled: essentialsFilled,      total: essentialsItems.length },
      personalization: { filled: personalizationFilled, total: personalizationItems.length },
      // Details tab is fully optional → no progress indicator.
      details:         { filled: 0, total: 0 },
    };
  }, [
    cloneConfig.uniqueAdvantage,
    cloneConfig.painPoint,
    cloneConfig.goal,
    cloneConfig.cloneId,
    cloneConfig.variants,
    enabledStandardKeys,
  ]);

  // Clone × template conflicts, computed once and distributed inline to the
  // exact control each one relates to (filtered by `field`) — instead of a
  // single noisy banner at the top of the editor.
  const templateConflicts = useMemo(
    () =>
      selectedClone && selectedClone.voiceDNA
        ? computeTemplateConflicts(selectedClone, cloneConfig)
        : [],
    [selectedClone, cloneConfig],
  );

  // Pure persistence — no UI side-effects. Used by both `handleSave` (with
  // user-facing messages) and `persistDraftSilent` (called before Generate
  // to make sure the latest state is in RTDB before the backend tries to
  // read the template).
  const buildPersistPayload = () => ({
    topic: (draft.topic || "").trim() || "Untitled template",
    type: draft.type || "intro",
    mode: TEMPLATE_MODES.VOICE_CLONE,
    isCustom: true,
    aiTagId: draft.aiTagId || "",
    callContext: normalizeCallContext(draft.callContext),
    cloneConfig: {
      cloneId: cloneConfig.cloneId || "",
      fieldConfig: {
        enabledStandardKeys: enabledStandardKeys || [],
        enabledCustomIds: Array.isArray(enabledCustomIds)
          ? enabledCustomIds
          : [],
      },
      enhancements,
      // fixedFacts / angles are legacy — preserved verbatim so old templates
      // keep their data. The Details tab now exposes a single free-text
      // `extraRules` field instead.
      fixedFacts: cloneConfig.fixedFacts || [],
      campaignTaboos: cloneConfig.campaignTaboos || [],
      angles: cloneConfig.angles || [],
      extraRules: cloneConfig.extraRules || "",
      // Structure source — 'twin' mirrors the CopyTwin's own structure (no
      // active variant required), 'custom' uses the configured variants. This
      // was previously omitted from the payload, so toggling "Copy from
      // CopyTwin" appeared to save but never persisted → on reload it fell
      // back to 'custom' and the backend demanded an active variant.
      structureSource: resolveStructureSource(cloneConfig),
      variants,
      // Campaign brief — the three answers that drive every email.
      references: cloneConfig.references || "",
      uniqueAdvantage: cloneConfig.uniqueAdvantage || "",
      painPoint: cloneConfig.painPoint || "",
      goal: cloneConfig.goal || "",
      // Optional info pools the user attached to this template. Each
      // entry: { id, type, label, icon, items: string[] }.
      infoAddons: cloneConfig.infoAddons || [],
      // Per-template paragraph-rhythm override. The backend reads this in
      // voiceCloneEmailPrompt.renderParagraphRhythm; without persisting it
      // here the picker would silently reset to "auto" on every reload.
      paragraphRhythm: cloneConfig.paragraphRhythm || "auto",
      // Language used by the generated emails (and the editor UI for
      // EN/DE). Persisted alongside the rest so Step 6 can read it
      // directly off the template just like Standard mode does.
      language: campaignLanguage,
      // Legacy back-compat — old templates may still hold these.
      audience: cloneConfig.audience || "",
      offer: cloneConfig.offer || "",
      audienceOverride: cloneConfig.audienceOverride || "",
      smartSubject: cloneConfig.smartSubject !== false, // default true
      // Two adherence dials persisted alongside the rest of cloneConfig — the
      // backend prompt builder reads them in voiceCloneEmailPrompt.js to emit
      // the matching directive blocks.
      voiceAdherence: cloneConfig.voiceAdherence || VOICE_ADHERENCE_DEFAULT,
      briefAdherence: cloneConfig.briefAdherence || BRIEF_ADHERENCE_DEFAULT,
      // Creativity (sampling temperature) dial — read by the backend generator
      // (generateVoiceCloneEmail.js → resolveCreativityTemperature).
      creativity: cloneConfig.creativity || CREATIVITY_DEFAULT,
      // Opt-in style/topic adaptation. Missing on legacy templates → false
      // (faithful clone), matching the backend's default interpretation.
      allowStyleAdaptation: cloneConfig.allowStyleAdaptation === true,
      qualityTier:
        resolveQualityTier(cloneConfig.qualityTier).id ||
        QUALITY_TIER_DEFAULT,
      // Per-template addressing controls — resolveSignOffIdentity /
      // resolveGreeting normalize legacy/missing shapes to the documented
      // defaults so the backend can rely on a stable structure regardless
      // of when the template was created.
      signOffIdentity: resolveSignOffIdentity(cloneConfig.signOffIdentity),
      greeting:        resolveGreeting(cloneConfig.greeting),
    },
    subjectLines,
    // keep the cross-mode tracking schema consistent with standard mode:
    // - stats.totalSent / totalReplies are written by the backend on send/reply
    // - stats.variantStats[variantId] is the per-structure tracker
    // We intentionally do NOT write stats here (avoid clobbering live data).
  });

  const writePersistPayload = async (payload) => {
    await update(
      ref(database, `/World/${userId}/settings/customTopics/${templateId}`),
      payload,
    );
    if (isSelected) {
      await update(
        ref(database, `/World/${userId}/settings/selectedTopics/${templateId}`),
        {
          topic: payload.topic,
          type: payload.type,
          mode: payload.mode,
          isCustom: true,
          aiTagId: payload.aiTagId,
          callContext: payload.callContext,
          cloneConfig: payload.cloneConfig,
          subjectLines: payload.subjectLines,
        },
      );
    }
  };

  // Silent persistence — used by Generate to make sure the latest draft is
  // written to RTDB before the backend tries to read the template. Throws
  // on validation failure so the caller can surface a clean error.
  const persistDraftSilent = async () => {
    if (!userId || !templateId) {
      throw new Error("Editor is not ready yet — try again in a second.");
    }
    if (!cloneConfig.cloneId) {
      throw new Error("Select a CopyTwin first.");
    }
    await writePersistPayload(buildPersistPayload());
  };

  // ---------- AUTO-SAVE ----------
  // Watches the draft for any change and debounces a write to RTDB. Replaces
  // the manual Save button. We compute a JSON signature of the persistable
  // payload so we don't fire on no-op renders (e.g. when only a transient
  // local UI state changes). The first run after hydration just records the
  // baseline signature without saving — otherwise we'd overwrite the loaded
  // template with itself on mount and trigger a "Saved" flash with no edit.
  useEffect(() => {
    if (!userId || !templateId) return;
    if (!cloneConfig.cloneId) {
      // Until a clone is picked, persistence is blocked at the validation
      // layer anyway — show a neutral "pick a clone" state instead.
      setAutoSaveStatus("idle");
      return;
    }

    let signature;
    try {
      signature = JSON.stringify(buildPersistPayload());
    } catch {
      return; // unserializable state — skip until next render
    }

    // First time we see a fully-validated payload → snapshot baseline,
    // don't write. Subsequent renders compare against this snapshot.
    if (lastSavedSignatureRef.current === null) {
      lastSavedSignatureRef.current = signature;
      setAutoSaveStatus("saved");
      return;
    }

    if (signature === lastSavedSignatureRef.current) return;

    // Something changed. Mark dirty immediately for instant feedback,
    // then debounce the actual write so a burst of edits collapses to one.
    setAutoSaveStatus("dirty");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (savedAtFadeTimerRef.current) clearTimeout(savedAtFadeTimerRef.current);

    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        await writePersistPayload(buildPersistPayload());
        lastSavedSignatureRef.current = signature;
        setSavedAt(Date.now());
        setAutoSaveStatus("saved");
      } catch (err) {
        console.error("[VoiceCloneTemplate] auto-save failed", err);
        setAutoSaveStatus("error");
        // Retry once after 4s — covers transient network blips without
        // requiring the user to do anything.
        savedAtFadeTimerRef.current = setTimeout(() => {
          setAutoSaveStatus("dirty"); // re-arm the watcher for the next render
        }, 4000);
      }
    }, 1200);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // We deliberately list `draft` (the only state that drives the payload)
    // plus the boundary variables so the effect re-runs on every meaningful
    // change. buildPersistPayload + writePersistPayload are stable closures
    // that close over current state — no need to depend on them directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    templateId,
    cloneConfig.cloneId,
    draft,
    isSelected,
  ]);

  // ---------- AUTO-SAVE DISPLAY DEBOUNCE ----------
  // Project the internal status onto the visible tag with two filters:
  //   1. "dirty" never reaches the UI — we keep showing the previous resting
  //      label (typically "Gespeichert") so the tag doesn't flicker per
  //      keystroke. The internal state still drives the write debounce.
  //   2. "saving" only surfaces if the write actually lingers past
  //      AUTO_SAVE_SHOW_SAVING_DELAY_MS. Sub-threshold writes stay invisible
  //      so the user never sees a 50ms spinner flash.
  // Terminal states ("saved" / "error") and lifecycle states ("idle" /
  // "disabled") reflect immediately so feedback isn't artificially delayed.
  useEffect(() => {
    if (displayDebounceRef.current) {
      clearTimeout(displayDebounceRef.current);
      displayDebounceRef.current = null;
    }

    if (autoSaveStatus === "dirty") return; // keep prior display

    if (autoSaveStatus === "saving") {
      displayDebounceRef.current = setTimeout(() => {
        setDisplayStatus("saving");
      }, AUTO_SAVE_SHOW_SAVING_DELAY_MS);
      return;
    }

    setDisplayStatus(autoSaveStatus);
  }, [autoSaveStatus]);

  // Cleanup on unmount — no orphaned timers writing to RTDB after the
  // editor is gone (e.g. user clicks Back mid-debounce).
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (savedAtFadeTimerRef.current) clearTimeout(savedAtFadeTimerRef.current);
      if (displayDebounceRef.current) clearTimeout(displayDebounceRef.current);
    };
  }, []);

  const handleDelete = async () => {
    if (!userId || !templateId) return;
    try {
      await remove(
        ref(database, `/World/${userId}/settings/customTopics/${templateId}`),
      );
      if (isSelected) {
        await remove(
          ref(database, `/World/${userId}/settings/selectedTopics/${templateId}`),
        );
      }
      onDeleted?.();
      onClose?.();
    } catch (err) {
      console.error("[VoiceCloneTemplate] delete failed", err);
      setMessage("Delete failed.");
    }
  };

  // ---------- Variant operations ----------

  const addVariant = (v) =>
    patchClone({ variants: [...variants, v] });

  const removeVariant = (id) =>
    patchClone({ variants: variants.filter((v) => v.id !== id) });

  const toggleVariantActive = (id) =>
    patchClone({
      variants: variants.map((v) =>
        v.id === id ? { ...v, active: !v.active } : v,
      ),
    });

  const cloneVariant = (v) => {
    // Resolve to handle both legacy `lengthId` and new `lengthTweakId` shapes.
    const tweakId = resolveLengthTweakId(v.lengthTweakId || v.lengthId);
    const fresh = createVariant(v.structureId, tweakId, v.customWords);
    if (!fresh) return;
    patchClone({ variants: [...variants, { ...fresh, id: generateVariantId() }] });
  };

  // Inline-edit the length tweak on an already-created variant. We drop the
  // legacy `lengthId` field if present so the resolver doesn't fall back to
  // a stale absolute length on the next render. The optional `extras` arg
  // lets callers (the Custom-tweak chip + word input) tweak `customWords`
  // alongside the tweak id in a single patch — otherwise we keep / clear
  // `customWords` based on whether the resolved tweak still needs it.
  const updateVariantLength = (id, tweakId, extras) => {
    const safeTweakId = resolveLengthTweakId(tweakId);
    const tweak = LENGTH_TWEAK_BY_ID[safeTweakId];
    const usesCustom = tweak?.multiplier === "custom";
    patchClone({
      variants: variants.map((v) => {
        if (v.id !== id) return v;
        const { lengthId: _legacyLengthId, customWords: prevCustomWords, ...rest } = v;
        const next = { ...rest, lengthTweakId: safeTweakId };
        if (usesCustom) {
          next.customWords = sanitizeCustomWords(
            extras?.customWords ?? prevCustomWords,
          );
        }
        return next;
      }),
    });
  };

  // ---------- Subject lines ----------

  const addSubjectLine = () =>
    patch({
      subjectLines: [
        ...subjectLines,
        { id: `sub_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, text: "" },
      ],
    });

  const updateSubjectLine = (i, text) =>
    patch({
      subjectLines: subjectLines.map((s, idx) =>
        idx === i ? { ...s, text } : s,
      ),
    });

  const removeSubjectLine = (i) =>
    patch({ subjectLines: subjectLines.filter((_, idx) => idx !== i) });

  // ---------- Field selection ----------

  const updateFieldConfig = (next) =>
    patchClone({
      fieldConfig: {
        enabledStandardKeys: next.enabledStandardKeys ?? enabledStandardKeys,
        enabledCustomIds:
          next.enabledCustomIds ??
          (Array.isArray(enabledCustomIds) ? enabledCustomIds : []),
      },
    });

  const toggleStandardField = (key) => {
    const set = new Set(enabledStandardKeys);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    updateFieldConfig({ enabledStandardKeys: Array.from(set) });
  };

  const toggleCustomField = (id) => {
    const current = Array.isArray(enabledCustomIds) ? enabledCustomIds : [];
    const set = new Set(current);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    updateFieldConfig({ enabledCustomIds: Array.from(set) });
  };

  const selectAllStandard = () =>
    updateFieldConfig({
      enabledStandardKeys: STANDARD_PERSONALIZATION_FIELDS.map((f) => f.key),
    });

  const resetStandardToBaseline = () =>
    updateFieldConfig({ enabledStandardKeys: DEFAULT_ENABLED_STANDARD_KEYS });

  // ---------- Enhancements ----------

  const toggleEnhancement = (id, on) =>
    patchClone({
      enhancements: { ...enhancements, [id]: on },
    });

  // ---------- Render ----------

  const trainedClones = useMemo(
    () => clones.filter((c) => !!c.voiceDNA),
    [clones],
  );
  const otherClones = useMemo(
    () => clones.filter((c) => !c.voiceDNA),
    [clones],
  );

  return (
    <div className="vct-root">
      {/* Sticky header */}
      <div className="vct-header">
        <div className="vct-header-left">
          <button
            type="button"
            className="vct-back"
            onClick={onClose}
            title={t("headerBack")}
          >
            <i className="fa-solid fa-arrow-left" />
            {t("headerBack")}
          </button>
          <div className="vct-header-titles">
            {/* Single-row layout: title input on the left, type pill inline
                to its right. Replaces the old stacked eyebrow + title pair
                so the header eats less vertical space and reads at a glance. */}
            <div className="vct-header-name-row">
              <input
                type="text"
                className="vct-header-name-input"
                value={draft.topic || ""}
                placeholder={t("headerNamePlaceholder")}
                onChange={(e) => patch({ topic: e.target.value })}
              />
              <span className="vct-header-type-pill">
                <i className="fa-solid fa-wand-magic-sparkles" />
                {t(`headerType_${draft.type || "default"}`)}
              </span>
            </div>
          </div>
        </div>

        <div className="vct-header-right">
          {totalSent > 0 && (
            <div className="vct-header-stats">
              <div className="vct-header-stat">
                <div className="vct-header-stat-num">{totalSent}</div>
                <div className="vct-header-stat-label">{t("headerStatSent")}</div>
              </div>
              <div className="vct-header-stat">
                <div className="vct-header-stat-num">{totalReplies}</div>
                <div className="vct-header-stat-label">{t("headerStatReplies")}</div>
              </div>
              <div className="vct-header-stat vct-header-stat-rate">
                <div className="vct-header-stat-num">
                  {overallRate.toFixed(1)}%
                </div>
                <div className="vct-header-stat-label">{t("headerStatRate")}</div>
              </div>
            </div>
          )}

          <button
            type="button"
            className={`vct-select-toggle ${isSelected ? "on" : "off"}`}
            onClick={() => onSelectionToggle?.(templateId, draft)}
          >
            <i className={`fa-solid ${isSelected ? "fa-toggle-on" : "fa-toggle-off"}`} />
            {isSelected ? t("headerToggleActive") : t("headerToggleInactive")}
          </button>

          {/* Auto-save indicator — passive status pill that replaces the
              old manual Save button. State machine:
                Visual states (driven by displayStatus, the debounced
                projection of the internal autoSaveStatus):
                  idle/saved → green check  (resting state, also during typing)
                  saving     → blue spinner (only if write > ~350ms)
                  error      → red triangle (auto-retries)
                  disabled   → muted        (no Voice Clone picked yet) */}
          <AutoSaveIndicator
            status={!cloneConfig.cloneId ? "disabled" : displayStatus}
            t={t}
          />
        </div>
      </div>

      {message && (
        <div className="vct-message">
          <i className="fa-solid fa-circle-info" />
          {message}
        </div>
      )}

      <EditorLangContext.Provider value={uiLang}>
      <div className="vct-body">
        <div className="vct-split">
          <div className="vct-split-left">

        {/* Tab navigation — three progressive categories with completion meter */}
        <EditorTabs
          active={activeTab}
          onChange={setActiveTab}
          progress={tabProgress}
        />

        {/* Clone × template conflicts are no longer shown as a top banner.
            Each one is rendered inline (subtle) directly under the control it
            relates to — see <InlineConflict field="…" /> in the sections
            below. computeTemplateConflicts() in cloneTemplateUtils lists every
            check. */}

        {/* ============================ TAB 1 — ESSENTIALS ============================ */}
        {activeTab === "essentials" && (
          <>
            {/* Voice Clone */}
            <Section
              icon="fa-fingerprint"
              title={t("voiceCloneTitle")}
              hint={t("voiceCloneHint")}
              accent="blue"
            >
              {clonesLoading ? (
                <div className="vct-loading">
                  <div className="vct-spinner" />
                  {t("voiceCloneLoading")}
                </div>
              ) : clones.length === 0 ? (
                <div
                  className="vct-empty-inline"
                  /* Translated string contains a single safe <strong> tag — no
                     user content is interpolated, so dangerouslySetInnerHTML is
                     safe here and lets us keep one i18n entry per message. */
                  dangerouslySetInnerHTML={{
                    __html:
                      `<i class="fa-solid fa-circle-info"></i> ` +
                      t("voiceCloneEmpty"),
                  }}
                />
              ) : (
                <>
                  {trainedClones.length > 0 && (
                    <div className="vct-clone-grid">
                      {trainedClones.map((c) => (
                        <CloneCard
                          key={c.id}
                          clone={c}
                          selected={cloneConfig.cloneId === c.id}
                          onSelect={(id) => patchClone({ cloneId: id })}
                        />
                      ))}
                    </div>
                  )}
                  {otherClones.length > 0 && (
                    <>
                      <div className="vct-subgroup-label">
                        {t("voiceCloneUntrained")}
                        <InfoTooltip text={t("voiceCloneUntrainedHint")} />
                      </div>
                      <div className="vct-clone-grid">
                        {otherClones.map((c) => (
                          <CloneCard
                            key={c.id}
                            clone={c}
                            selected={cloneConfig.cloneId === c.id}
                            onSelect={(id) => patchClone({ cloneId: id })}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {selectedClone && (
                <div className="vct-clone-summary">
                  <div className="vct-clone-summary-icon">
                    <i className="fa-solid fa-check" />
                  </div>
                  <div>
                    <strong>{selectedClone.name}</strong>{" "}
                    {t("voiceCloneSelectedSuffix")}
                    {selectedClone.voiceDNA?.voiceFingerprint && (
                      <p className="vct-clone-summary-fingerprint">
                        "{selectedClone.voiceDNA.voiceFingerprint}"
                      </p>
                    )}
                  </div>
                </div>
              )}
              <InlineConflict conflicts={templateConflicts} field="identity" />
              {selectedClone && (
                <CloneVoiceDescriptionField
                  userId={userId}
                  clone={selectedClone}
                />
              )}
            </Section>

            {/* Language — sits directly under Voice Clone */}
            <Section
              icon="fa-globe"
              title={t("languageTitle")}
              hint={t("languageHint")}
              accent="blue"
            >
              <LanguagePicker
                value={campaignLanguage}
                onChange={(v) => patchClone({ language: v })}
              />
            </Section>

            {/* Paragraph rhythm — per-campaign override on top of the clone's
                learned paragraph style. Default 'auto' lets DNA win. */}
            <Section
              icon="fa-align-left"
              title={t("rhythmTitle")}
              hint={t("rhythmHint")}
              accent="blue"
            >
              <ParagraphRhythmPicker
                value={cloneConfig.paragraphRhythm || "auto"}
                onChange={(v) => patchClone({ paragraphRhythm: v })}
              />
              <InlineConflict conflicts={templateConflicts} field="paragraphRhythm" />
            </Section>

            {/* Campaign brief — SPECIAL / PAIN / GOAL (REFS lives in
                Details since it's enrichment, not a must-have).
                Recipient-context (WHO) is intentionally not here — that's
                per-contact and the AI gets it from the World table at send
                time. The brief covers what we want the AI to PULL FROM. */}
            <Section
              icon="fa-clipboard-list"
              title={t("briefTitle")}
              hint={t("briefHint")}
              accent="green"
            >
              <CampaignBrief
                uniqueAdvantage={cloneConfig.uniqueAdvantage || ""}
                painPoint={cloneConfig.painPoint || ""}
                goal={cloneConfig.goal || ""}
                onChange={patchClone}
              />
              <InlineConflict conflicts={templateConflicts} field="goal" />
            </Section>

            {/* Info add-ons — flexible info pools the user can attach to
                this template (case studies, testimonials, stats…). */}
            <Section
              icon="fa-folder-plus"
              title={t("addonsTitle")}
              hint={t("addonsHint")}
              accent="purple"
              count={(cloneConfig.infoAddons || []).length || null}
            >
              <InfoAddons
                addons={cloneConfig.infoAddons || []}
                onChange={(next) => patchClone({ infoAddons: next })}
              />
            </Section>

            {/* Structure variants — the centerpiece */}
            <Section
              icon="fa-flask"
              title={t("structuresTitle")}
              hint={t("structuresHint")}
              accent="purple"
              count={isTwinStructure ? null : `${activeCount}/${variants.length}`}
            >
              {/* Structure source — default switch between copying the
                  CopyTwin's own structure and using custom A/B variants. */}
              <StructureSourceToggle
                value={structureSource}
                onChange={(src) => patchClone({ structureSource: src })}
                cloneIsTrained={!!selectedClone?.voiceDNA}
                t={t}
              />

              <VariantBuilder
                variants={variants}
                stats={variantStats}
                onAdd={addVariant}
                onRemove={removeVariant}
                onToggleActive={toggleVariantActive}
                onClone={cloneVariant}
                onChangeLength={updateVariantLength}
                cloneAvgWords={cloneAvgWords}
                cloneIsTrained={!!selectedClone?.voiceDNA}
                clone={selectedClone}
                optional={isTwinStructure}
                optionalLabel={t("structureVariantsOptional")}
              />
            </Section>
          </>
        )}

        {/* ====================== TAB 2 — PERSONALIZATION ====================== */}
        {activeTab === "personalization" && (
          <>
            {/* Sign-off identity — per-template override for the sender name
                used in the prompt. When workspace `user_name` is empty we
                used to leak literal "Sender" into the closer; this section
                gives the user an explicit fix at the template level. */}
            <Section
              icon="fa-signature"
              title={t("signOffIdentityTitle")}
              hint={t("signOffIdentityHint")}
              accent="blue"
            >
              <SignOffIdentityPanel
                value={cloneConfig.signOffIdentity}
                onChange={(next) => patchClone({ signOffIdentity: next })}
              />
              <InlineConflict conflicts={templateConflicts} field="signOffIdentity" />
            </Section>

            {/* Greeting rules — pin exact openings per case (DM known vs
                only generic address). Auto keeps the legacy DNA-driven
                behavior so existing templates render unchanged. */}
            <Section
              icon="fa-comment-dots"
              title={t("greetingTitle")}
              hint={t("greetingHint")}
              accent="green"
            >
              <GreetingRulesPanel
                value={cloneConfig.greeting}
                onChange={(next) => patchClone({ greeting: next })}
              />
              <InlineConflict conflicts={templateConflicts} field="greeting" />
            </Section>

            <Section
              icon="fa-database"
              title={t("contactDataTitle")}
              hint={t("contactDataHint")}
              accent="purple"
            >
              <FieldSelector
                enabledStandardKeys={enabledStandardKeys}
                enabledCustomIds={
                  Array.isArray(enabledCustomIds) ? enabledCustomIds : []
                }
                customFields={customFields}
                customFieldsLoading={customFieldsLoading}
                onToggleStandard={toggleStandardField}
                onToggleCustom={toggleCustomField}
                onSelectAllStandard={selectAllStandard}
                onResetStandard={resetStandardToBaseline}
              />
            </Section>

            <Section
              icon="fa-phone-volume"
              title={t("callContextTitle")}
              hint={t("callContextHint")}
              accent="green"
            >
              <CallContextSettings
                embedded
                value={draft.callContext}
                onChange={(next) => setDraft((prev) => ({ ...prev, callContext: next }))}
              />
            </Section>

            <Section
              icon="fa-bolt"
              title={t("enhancementsTitle")}
              hint={t("enhancementsHint")}
              accent="blue"
            >
              <EnhancementsPanel
                enhancements={enhancements}
                onToggle={toggleEnhancement}
              />
            </Section>
          </>
        )}

        {/* ============================ TAB 3 — DETAILS ============================ */}
        {activeTab === "details" && (
          <>
            {/* References — proof points / clients / case studies the AI
                may pull from. Lives in Details (not Essentials) because
                it's a "nice to have" enrichment, not a must-have to send.
                Backend wiring is unchanged — `cloneConfig.references` is
                still read by voiceCloneEmailPrompt.renderBrief and by the
                subject prompt. */}
            <Section
              icon="fa-quote-left"
              title={t("briefRefsLabel")}
              hint={t("briefRefsMicro")}
              accent="blue"
            >
              <textarea
                className="vct-textarea"
                rows={3}
                value={cloneConfig.references || ""}
                placeholder={t("briefRefsPlaceholder")}
                onChange={(e) => patchClone({ references: e.target.value })}
              />
            </Section>

            {/* Extra rules — free-text bucket the user can use to pin
                additional must-follow rules / notes on top of the campaign
                brief. Replaces the old facts + angles chip lists with a
                single, more expressive field. Legacy templates may still
                hold `fixedFacts` / `angles` arrays — the backend keeps
                rendering them so existing campaigns are not broken. */}
            <Section
              icon="fa-list-check"
              title={t("extraRulesTitle")}
              hint={t("extraRulesHint")}
              accent="purple"
            >
              <textarea
                className="vct-textarea"
                rows={4}
                value={cloneConfig.extraRules || ""}
                placeholder={t("extraRulesPlaceholder")}
                onChange={(e) => patchClone({ extraRules: e.target.value })}
              />
            </Section>

            {/* Adherence dials — Voice & Brief. Two independent strictness
                levels that drive the prompt's directives at generation time.
                Defaults are "balanced" so existing templates keep behaving
                like before. */}
            <Section
              icon="fa-sliders"
              title={t("adherenceTitle")}
              hint={t("adherenceHint")}
              accent="blue"
            >
              <div className="vct-adherence-stack">
                <div className="vct-adherence-row">
                  <div className="vct-adherence-row-head">
                    <span className="vct-adherence-row-label">
                      {t("voiceAdherenceLabel")}
                    </span>
                    <span className="vct-adherence-row-hint">
                      {t("voiceAdherenceHint")}
                    </span>
                  </div>
                  <AdherencePicker
                    levels={VOICE_ADHERENCE_LEVELS}
                    value={cloneConfig.voiceAdherence || VOICE_ADHERENCE_DEFAULT}
                    onChange={(v) => patchClone({ voiceAdherence: v })}
                    keyPrefix="voice"
                    ariaLabel={t("voiceAdherenceLabel")}
                  />
                  <InlineConflict conflicts={templateConflicts} field="voiceAdherence" />
                </div>

                <div className="vct-adherence-row">
                  <div className="vct-adherence-row-head">
                    <span className="vct-adherence-row-label">
                      {t("briefAdherenceLabel")}
                    </span>
                    <span className="vct-adherence-row-hint">
                      {t("briefAdherenceHint")}
                    </span>
                  </div>
                  <AdherencePicker
                    levels={BRIEF_ADHERENCE_LEVELS}
                    value={cloneConfig.briefAdherence || BRIEF_ADHERENCE_DEFAULT}
                    onChange={(v) => patchClone({ briefAdherence: v })}
                    keyPrefix="brief"
                    ariaLabel={t("briefAdherenceLabel")}
                  />
                  <InlineConflict conflicts={templateConflicts} field="briefAdherence" />
                </div>

                {/* Creativity (sampling temperature). Hidden in "mirror" mode:
                    Mirror forces maximum voice fidelity, so a creativity dial
                    there has almost no room to act — it only makes sense once
                    the voice is dialed below mirror (free / balanced / strict). */}
                {(cloneConfig.voiceAdherence || VOICE_ADHERENCE_DEFAULT) !== "mirror" && (
                  <div className="vct-adherence-row">
                    <div className="vct-adherence-row-head">
                      <span className="vct-adherence-row-label">
                        {t("creativityLabel")}
                      </span>
                      <span className="vct-adherence-row-hint">
                        {t("creativityHint")}
                      </span>
                    </div>
                    <AdherencePicker
                      levels={CREATIVITY_LEVELS}
                      value={cloneConfig.creativity || CREATIVITY_DEFAULT}
                      onChange={(v) => patchClone({ creativity: v })}
                      keyPrefix="creativity"
                      ariaLabel={t("creativityLabel")}
                    />
                  </div>
                )}

                {/* Style / topic adaptation — opt-in escape hatch from faithful
                    cloning. OFF (default) keeps subject + body locked to the
                    writer's trained style; ON lets the AI drift to fit a new
                    topic/industry, with a warning that the style may change. */}
                <div className="vct-adherence-row">
                  <button
                    type="button"
                    className={`vct-toggle-row ${cloneConfig.allowStyleAdaptation ? "danger" : "off"}`}
                    onClick={() => {
                      const turningOn = !cloneConfig.allowStyleAdaptation;
                      // Guard the ON direction: this setting has a drastic effect
                      // on output (faithful clone → adaptive drift), so require an
                      // explicit confirm so it can never be flipped by accident.
                      if (turningOn) {
                        const ok = window.confirm(
                          "Allow style / topic adaptation?\n\n" +
                          "⚠️ This DRASTICALLY changes your results. The AI is " +
                          "allowed to drift away from your trained voice and can " +
                          "produce generic, off-brand emails that no longer sound " +
                          "like you or your uploaded examples.\n\n" +
                          "Only enable this if the campaign topic has genuinely " +
                          "changed. For a faithful clone, keep it OFF.",
                        );
                        if (!ok) return;
                      }
                      patchClone({ allowStyleAdaptation: turningOn });
                    }}
                  >
                    <i
                      className={`fa-solid ${
                        cloneConfig.allowStyleAdaptation ? "fa-toggle-on" : "fa-toggle-off"
                      }`}
                    />
                    <div className="vct-toggle-row-body">
                      <div className="vct-toggle-row-title">
                        Allow style / topic adaptation
                        {cloneConfig.allowStyleAdaptation ? (
                          <span className="vct-badge-danger">High impact</span>
                        ) : (
                          <span className="vct-badge-recommended">
                            Recommended: Off
                          </span>
                        )}
                      </div>
                      <div className="vct-toggle-row-hint">
                        Off (recommended): the clone copies your trained subject
                        and body style as-is, even if the recipient&apos;s
                        industry differs a little. Turn this on only when the
                        campaign topic has genuinely changed and the AI should
                        adapt.
                      </div>
                    </div>
                  </button>

                  {cloneConfig.allowStyleAdaptation ? (
                    <div className="vct-adapt-warning" role="alert">
                      <i className="fa-solid fa-triangle-exclamation" />
                      <span>
                        <strong>
                          This drastically changes your results.
                        </strong>{" "}
                        With adaptation on, the AI may drift from your calibrated
                        voice to fit the new topic — your subject lines and emails
                        can stop sounding like you and slide into generic
                        phrasing. Turn it off to keep a faithful clone.
                      </span>
                    </div>
                  ) : (
                    <div className="vct-faithful-note">
                      <i className="fa-solid fa-shield-halved" />
                      <span>
                        <strong>Faithful clone active.</strong> Emails stay true
                        to your trained style and charm.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Section>

            <Section
              icon="fa-heading"
              title={t("subjectsTitle")}
              hint={t("subjectsHint")}
              accent="blue"
            >
              <SubjectLinesList
                subjectLines={subjectLines}
                onAdd={addSubjectLine}
                onUpdate={updateSubjectLine}
                onRemove={removeSubjectLine}
                smartEnabled={cloneConfig.smartSubject !== false}
                onToggleSmart={(on) => patchClone({ smartSubject: on })}
              />
              <InlineConflict conflicts={templateConflicts} field="smartSubject" />
            </Section>

            <Section
              icon="fa-ban"
              title={t("taboosTitle")}
              hint={t("taboosHint")}
              accent="red"
              count={(cloneConfig.campaignTaboos || []).length}
            >
              <ChipList
                items={cloneConfig.campaignTaboos || []}
                accent="red"
                placeholder={t("taboosPlaceholder")}
                onAdd={(v) =>
                  patchClone({
                    campaignTaboos: [
                      ...(cloneConfig.campaignTaboos || []),
                      v,
                    ],
                  })
                }
                onRemove={(i) =>
                  patchClone({
                    campaignTaboos: (cloneConfig.campaignTaboos || []).filter(
                      (_, idx) => idx !== i,
                    ),
                  })
                }
              />
            </Section>

            {/* Danger zone */}
            <div className="vct-danger-zone">
              <div className="vct-danger-zone-body">
                <div className="vct-danger-zone-title">{t("dangerTitle")}</div>
                <div className="vct-danger-zone-hint">{t("dangerHint")}</div>
              </div>
              <button
                type="button"
                className="vct-btn-danger"
                onClick={() => setConfirmDelete(true)}
              >
                <i className="fa-regular fa-trash-can" /> {t("dangerBtn")}
              </button>
            </div>
          </>
        )}

          </div>
          {/* RIGHT — sticky live preview (Step 6 will wire it to a Haiku Cloud Function) */}
          <aside className="vct-split-right">
            <LivePreviewPanel
              userId={userId}
              templateId={templateId}
              clone={selectedClone}
              cloneConfig={cloneConfig}
              templateType={draft.type}
              templateName={draft.topic}
              onQualityChange={(tierId) =>
                patchClone({ qualityTier: tierId })
              }
              ensurePersisted={persistDraftSilent}
            />
          </aside>
        </div>
      </div>
      </EditorLangContext.Provider>

      {confirmDelete && (
        <div
          className="vct-confirm-overlay"
          onClick={() => setConfirmDelete(false)}
        >
          <div className="vct-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="vct-confirm-icon">
              <i className="fa-regular fa-trash-can" />
            </div>
            <div className="vct-confirm-title">{t("confirmTitle")}</div>
            <div className="vct-confirm-text">{t("confirmText")}</div>
            <div className="vct-confirm-actions">
              <button
                type="button"
                className="vct-btn-ghost"
                onClick={() => setConfirmDelete(false)}
              >
                {t("confirmCancel")}
              </button>
              <button
                type="button"
                className="vct-btn-danger"
                onClick={handleDelete}
              >
                {t("confirmDelete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceCloneTemplateEditor;
